
import React, { useState, useEffect, useRef } from 'react';
import { Homework, HomeworkTask, AcademicYear, ClassRoom, UserProfile, WeeklyLog, StudentConnection, CalendarEvent, InvitationCode, PreReportedAbsence, Organization, CalendarEventAttachment, EventType, Announcement, InvitationType, Gender, OrgType, ApprovalStatus } from '../types';
import { Type } from '@google/genai';
import { generateContentWithRetry } from '../lib/gemini';
import { ImageUpload, VoiceInput, FileAttachmentButton, Avatar, LanguageSelector, PhoneInput, GenderSelect, AttachmentButton } from './common';
import { COUNTRIES_STATES, COUNTRY_CODES } from '../lib/constants';
import { sanitizePersonName, sanitizeEntityName, validatePhoneNumber, getAttachmentType, getAttachmentFileName } from '../lib/helpers';
import { uploadHomeworkAttachment, validateHomeworkFile, uploadImageFromBase64 } from '../firebaseUtils';
import { isStaffFeatureEnabled } from '../lib/features';
import { SmartCalendar } from './attendance';
import { useCheckinTranslation } from '../hooks/useCheckinTranslation';
import { CheckinTranslationBar } from './CheckinTranslationBar';
import { ListenButton } from './ListenButton';
import { OPTION_KEY_TO_ENGLISH } from '../lib/checkinTranslations';
import { TransferOwnershipModal } from './TransferOwnershipModal';

// --- PolicyModal ---
export const PolicyModal = ({ onClose }: { onClose: () => void }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Privacy Policy</h2>
                <button onClick={onClose}><span className="material-icons-round">close</span></button>
            </div>
            <div className="text-sm text-gray-600 space-y-2">
                <p><strong>Data Collection:</strong> We collect minimal data necessary for educational purposes: names, attendance, homework logs, and communication history.</p>
                <p><strong>AI Usage:</strong> Our AI features (translation, insights, tutor) process data ephemerally. We do not use your data to train public models.</p>
                <p><strong>Security:</strong> All data is encrypted in transit and at rest using Firebase security standards.</p>
                <p><strong>Contact:</strong> For data deletion requests, contact your school administrator.</p>
            </div>
        </div>
    </div>
);

// --- CreateHomeworkModal ---
export const CreateHomeworkModal = ({ onClose, onSave, onUpdate, onDelete, homeworkToEdit, t, prefill, allClassHomeworks }: { 
    onClose: () => void, 
    onSave: (tasks: HomeworkTask[], date: string, attachments: string[]) => void, 
    onUpdate?: (id: string, tasks: HomeworkTask[], date: string, attachments?: string[]) => void, 
    onDelete?: (id: string) => Promise<void>,
    homeworkToEdit?: Homework | null, 
    t: (key: string) => string, 
    prefill?: string,
    allClassHomeworks?: Homework[] 
}) => {
    const [date, setDate] = useState(homeworkToEdit?.date || new Date().toISOString().split('T')[0]);
    const [tasks, setTasks] = useState<HomeworkTask[]>(homeworkToEdit?.tasks || []);
    const [attachments, setAttachments] = useState<string[]>(homeworkToEdit?.attachments || []);
    const [rawText, setRawText] = useState(prefill || '');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<string | null>(null);
    const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (prefill && tasks.length === 0) {
            handleAIProcess(prefill);
        }
    }, []);

    const handleAIProcess = async (input: string) => {
        setIsProcessing(true);
        try {
            const prompt = `Convert this homework text into a list of structured tasks.
            Input: "${input}"
            Return JSON object with a "tasks" array. Each task has: subject (string), description (string), estimatedTime (number, minutes).`;
            
            const response = await generateContentWithRetry({
                model: 'gemini-3-flash-preview',
                contents: prompt,
                config: { responseMimeType: 'application/json' }
            });
            
            const data = JSON.parse(response.text || '{}');
            if (data.tasks) {
                const newTasks = data.tasks.map((t: any) => ({
                    id: Math.random().toString(36).substr(2, 9),
                    ...t
                }));
                setTasks(prev => [...prev, ...newTasks]);
                setRawText(''); // Clear raw text on success
            }
        } catch (e) {
            console.error("AI processing failed", e);
            alert("Could not process text automatically. Please add tasks manually.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files;
        if (!fileList || fileList.length === 0) return;
        const files: File[] = Array.from(fileList);
        e.target.value = '';

        setIsUploading(true);
        const uploadedUrls: string[] = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            setUploadProgress(`Uploading ${i + 1}/${files.length}: ${file.name}...`);
            const validation = validateHomeworkFile(file);
            if (!validation.valid) {
                alert(validation.error || `File ${file.name} cannot be uploaded.`);
                continue;
            }

            try {
                const url = await uploadHomeworkAttachment(file, 'homework');
                uploadedUrls.push(url);
            } catch (err: any) {
                console.error("Upload failed for file:", file.name, err);
                alert(`Upload failed for "${file.name}": ${err.message || 'Storage error'}`);
            }
        }

        if (uploadedUrls.length > 0) {
            setAttachments(prev => [...prev, ...uploadedUrls]);
        }
        setIsUploading(false);
        setUploadProgress(null);
    };

    const handleSave = async () => {
        if (tasks.length === 0 || isUploading) return;

        // Automatically convert any legacy data: URLs to Cloud Storage URLs before saving
        let finalAttachments = [...attachments];
        const hasBase64 = finalAttachments.some(url => url && url.startsWith('data:'));
        if (hasBase64) {
            setIsUploading(true);
            setUploadProgress("Safely saving attachments to Cloud Storage...");
            try {
                finalAttachments = await Promise.all(finalAttachments.map(async (att, idx) => {
                    if (att && att.startsWith('data:')) {
                        const path = `homework/legacy_${Date.now()}_${idx}`;
                        return await uploadImageFromBase64(att, path);
                    }
                    return att;
                }));
            } catch (err) {
                console.warn("Base64 migration warning:", err);
            } finally {
                setIsUploading(false);
                setUploadProgress(null);
            }
        }

        if (homeworkToEdit && onUpdate) {
            onUpdate(homeworkToEdit.id, tasks, date, finalAttachments);
        } else {
            onSave(tasks, date, finalAttachments);
        }
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] flex flex-col">
                <h2 className="text-xl font-bold mb-4">{homeworkToEdit ? 'Edit Homework' : t('assign_homework')}</h2>
                
                <div className="flex-1 overflow-y-auto space-y-4">
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">{t('homework_for_date')}</label>
                        <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full p-2 border rounded mt-1" />
                    </div>

                    {/* Attachments (Cloud Storage Enabled) */}
                    <div>
                        <div className="flex justify-between items-center">
                            <label className="text-xs font-bold text-gray-500 uppercase">
                                Attachments & Whiteboard Photos (Max 10MB)
                            </label>
                            {attachments.length > 0 && (
                                <span className="text-xs text-gray-400 font-medium">
                                    {attachments.length} {attachments.length === 1 ? 'file' : 'files'}
                                </span>
                            )}
                        </div>

                        {/* Hidden native input for multi-file selection */}
                        <input 
                            type="file" 
                            ref={fileInputRef} 
                            onChange={handleFilesSelected} 
                            multiple 
                            accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain" 
                            className="hidden" 
                        />

                        <div className="flex flex-wrap gap-2 mt-2">
                            {attachments.map((url, idx) => {
                                const type = getAttachmentType(url);
                                const filename = getAttachmentFileName(url);

                                return (
                                    <div key={idx} className="relative group border rounded-lg overflow-hidden bg-gray-50 w-20 h-20 flex flex-col items-center justify-center p-1">
                                        {type === 'image' ? (
                                            <img 
                                                src={url} 
                                                alt="attachment" 
                                                className="w-full h-full object-cover rounded cursor-pointer" 
                                                onClick={() => setPreviewImageUrl(url)}
                                            />
                                        ) : (
                                            <a 
                                                href={url} 
                                                target="_blank" 
                                                rel="noopener noreferrer" 
                                                className="flex flex-col items-center justify-center text-center w-full h-full text-xs text-gray-700 hover:text-indigo-600 p-1"
                                                title={filename}
                                            >
                                                <span className={`material-icons-round text-2xl ${type === 'pdf' ? 'text-red-500' : 'text-blue-500'}`}>
                                                    {type === 'pdf' ? 'picture_as_pdf' : 'description'}
                                                </span>
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mt-0.5">
                                                    {type === 'pdf' ? 'PDF' : 'DOC'}
                                                </span>
                                            </a>
                                        )}
                                        <button 
                                            onClick={() => setAttachments(attachments.filter((_, i) => i !== idx))} 
                                            className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 shadow transition-colors"
                                            title="Remove attachment"
                                        >
                                            <span className="material-icons-round text-xs leading-none">close</span>
                                        </button>
                                    </div>
                                );
                            })}

                            {/* Add File / Photo Button */}
                            <button
                                type="button"
                                onClick={() => !isUploading && fileInputRef.current?.click()}
                                disabled={isUploading}
                                className={`w-20 h-20 border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-colors ${
                                    isUploading 
                                        ? 'border-indigo-300 bg-indigo-50 text-indigo-400 cursor-wait' 
                                        : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50 text-gray-400 hover:text-indigo-600'
                                }`}
                                title="Attach photo, worksheet, PDF or document"
                            >
                                {isUploading ? (
                                    <span className="material-icons-round text-xl animate-spin">sync</span>
                                ) : (
                                    <>
                                        <span className="material-icons-round text-xl">cloud_upload</span>
                                        <span className="text-[10px] font-semibold mt-1">Attach</span>
                                    </>
                                )}
                            </button>
                        </div>

                        {uploadProgress && (
                            <p className="text-xs text-indigo-600 font-medium mt-1 animate-pulse">
                                {uploadProgress}
                            </p>
                        )}
                        <p className="text-[11px] text-gray-400 mt-1">
                            Supported: Photos (JPG, PNG), Worksheets, PDFs & Word docs up to 10MB.
                        </p>
                    </div>

                    {/* AI Input */}
                    <div className="bg-gray-50 p-3 rounded-lg border">
                        <textarea 
                            value={rawText}
                            onChange={e => setRawText(e.target.value)}
                            placeholder={t('type_or_speak_homework')}
                            className="w-full bg-transparent border-none outline-none text-sm min-h-[60px]"
                        />
                        <div className="flex justify-between items-center mt-2">
                            <VoiceInput onTranscriptReceived={text => setRawText(prev => prev + ' ' + text)} onProcessingChange={setIsProcessing} />
                            <button onClick={() => handleAIProcess(rawText)} disabled={!rawText || isProcessing} className="text-xs bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full font-bold disabled:opacity-50">
                                {isProcessing ? t('processing') : t('process_with_ai')}
                            </button>
                        </div>
                    </div>

                    {/* Task List */}
                    <div className="space-y-2">
                        {tasks.map((task, idx) => (
                            <div key={task.id} className="flex gap-2 items-start bg-white p-2 border rounded">
                                <div className={`w-2 h-full self-stretch rounded-l ${['bg-red-400','bg-blue-400','bg-green-400'][idx%3]}`}></div>
                                <div className="flex-1">
                                    <input 
                                        value={task.subject} 
                                        onChange={e => {
                                            const newTasks = [...tasks];
                                            newTasks[idx].subject = e.target.value;
                                            setTasks(newTasks);
                                        }}
                                        className="font-bold text-sm w-full outline-none"
                                        placeholder="Subject"
                                    />
                                    <textarea 
                                        value={task.description}
                                        onChange={e => {
                                            const newTasks = [...tasks];
                                            newTasks[idx].description = e.target.value;
                                            setTasks(newTasks);
                                        }}
                                        className="text-sm w-full outline-none resize-none"
                                        rows={2}
                                        placeholder="Description"
                                    />
                                </div>
                                <button onClick={() => setTasks(tasks.filter((_, i) => i !== idx))} className="text-gray-400 hover:text-red-500"><span className="material-icons-round">delete</span></button>
                            </div>
                        ))}
                    </div>
                    <button onClick={() => setTasks([...tasks, { id: Math.random().toString(), subject: '', description: '', estimatedTime: 15 }])} className="w-full py-2 border-2 border-dashed border-gray-300 text-gray-500 rounded-lg font-bold hover:bg-gray-50">+ Add Task</button>
                    {homeworkToEdit && onDelete && (
                        <button 
                            onClick={async () => {
                                if (window.confirm("Are you sure you want to completely delete this homework?")) {
                                    await onDelete(homeworkToEdit.id);
                                    onClose();
                                }
                            }}
                            className="w-full py-2 mt-2 bg-red-50 text-red-600 rounded-lg font-bold hover:bg-red-100"
                        >
                            Delete Homework
                        </button>
                    )}
                </div>

                <div className="flex gap-3 mt-4 pt-4 border-t">
                    <button onClick={onClose} className="flex-1 py-3 bg-gray-100 rounded-lg font-bold">{t('cancel')}</button>
                    <button onClick={handleSave} disabled={tasks.length === 0 || isUploading} className="flex-1 py-3 bg-indigo-600 text-white rounded-lg font-bold disabled:opacity-50">
                        {isUploading ? 'Uploading...' : t('post_homework')}
                    </button>
                </div>
            </div>

            {/* Fullscreen Image Preview Lightbox */}
            {previewImageUrl && (
                <div 
                    className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4"
                    onClick={() => setPreviewImageUrl(null)}
                >
                    <div className="relative max-w-4xl max-h-[90vh] flex flex-col items-center" onClick={e => e.stopPropagation()}>
                        <img 
                            src={previewImageUrl} 
                            alt="Attachment preview" 
                            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" 
                        />
                        <div className="flex items-center gap-3 mt-3">
                            <a 
                                href={previewImageUrl} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="px-4 py-2 bg-white text-gray-800 text-xs font-semibold rounded-full shadow hover:bg-gray-100 flex items-center gap-1"
                            >
                                <span className="material-icons-round text-sm">open_in_new</span>
                                Open Original
                            </a>
                            <button 
                                onClick={() => setPreviewImageUrl(null)}
                                className="px-4 py-2 bg-gray-800 text-white text-xs font-semibold rounded-full shadow hover:bg-gray-700"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- EditProfileModal ---
export const EditProfileModal = ({ 
    user, 
    onClose, 
    onSave, 
    t, 
    onDeleteAccount 
}: { 
    user: UserProfile, 
    onClose: () => void, 
    onSave: (id: string, data: Partial<UserProfile>) => Promise<void> | void, 
    t: (k: string) => string, 
    onDeleteAccount?: () => void 
}) => {
    const [name, setName] = useState(user.name || '');
    const [photoUrl, setPhotoUrl] = useState(user.photoUrl || '');
    const [language, setLanguage] = useState(user.language || 'en');
    const [mobileNumber, setMobileNumber] = useState(user.mobileNumber || '');
    const [gender, setGender] = useState<Gender>(user.gender || 'other');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    const handleSave = async () => {
        setError('');
        if (!name.trim()) {
            setError('Full name is required.');
            return;
        }
        if (mobileNumber && !validatePhoneNumber(mobileNumber)) {
            setError('Please enter a valid phone number with country code.');
            return;
        }

        setIsSaving(true);
        try {
            await onSave(user.id, { 
                name: sanitizePersonName(name), 
                photoUrl, 
                language, 
                mobileNumber: mobileNumber.trim(),
                gender,
                updatedAt: new Date()
            });
            setIsSaving(false);
            onClose();
        } catch (err: any) {
            setIsSaving(false);
            setError(err?.message || 'Failed to update profile.');
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-7 border border-slate-100 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
                            <span className="material-icons-round text-lg">person</span>
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-900 leading-tight">{t('edit_profile')}</h2>
                            <p className="text-xs text-slate-500">Manage your personal account details</p>
                        </div>
                    </div>
                    <button 
                        onClick={onClose} 
                        className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors"
                    >
                        <span className="material-icons-round text-base">close</span>
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl flex items-center gap-2">
                        <span className="material-icons-round text-sm flex-shrink-0">error</span>
                        <span>{error}</span>
                    </div>
                )}

                <div className="space-y-4 text-xs">
                    <ImageUpload currentImage={photoUrl} onImageUpload={setPhotoUrl} label={t('your_photo')} />
                    
                    <div>
                        <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wide block mb-1">
                            {t('your_full_name')} *
                        </label>
                        <input 
                            type="text" 
                            value={name} 
                            onChange={e => setName(e.target.value)} 
                            placeholder="Enter your full name" 
                            className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-semibold text-slate-900 bg-white focus:ring-2 focus:ring-indigo-500 outline-none" 
                        />
                    </div>

                    {/* Email Address - Strictly Read-Only */}
                    <div>
                        <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wide flex items-center justify-between mb-1">
                            <span>Email Address</span>
                            <span className="text-[10px] text-slate-400 font-semibold flex items-center gap-0.5">
                                <span className="material-icons-round text-[12px]">lock</span>
                                Locked
                            </span>
                        </label>
                        <div className="relative">
                            <input 
                                type="email" 
                                value={user.email} 
                                readOnly 
                                disabled
                                className="w-full p-2.5 pl-8 border border-slate-200 rounded-xl text-xs font-medium text-slate-500 bg-slate-100 cursor-not-allowed select-none" 
                            />
                            <span className="material-icons-round text-slate-400 text-xs absolute left-2.5 top-3">email</span>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1 italic">
                            Email is linked to your authentication provider and cannot be modified.
                        </p>
                    </div>

                    <div>
                        <PhoneInput value={mobileNumber} onChange={setMobileNumber} label={t('phone_number')} />
                    </div>

                    <div>
                        <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wide block mb-1">
                            Gender
                        </label>
                        <GenderSelect value={gender} onChange={setGender} />
                    </div>

                    <div>
                        <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wide block mb-1">
                            {t('preferred_language')}
                        </label>
                        <LanguageSelector selectedLanguage={language} onSelectLanguage={setLanguage} />
                    </div>
                </div>

                <div className="flex gap-3 mt-6 pt-4 border-t border-slate-100">
                    <button 
                        onClick={onClose} 
                        disabled={isSaving}
                        className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors"
                    >
                        {t('cancel')}
                    </button>
                    <button 
                        onClick={handleSave} 
                        disabled={isSaving}
                        className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-colors shadow-xs flex items-center justify-center gap-1.5 disabled:opacity-50"
                    >
                        {isSaving && <span className="material-icons-round text-sm animate-spin">refresh</span>}
                        <span>{isSaving ? 'Saving...' : t('save_changes')}</span>
                    </button>
                </div>

                {onDeleteAccount && (
                    <div className="mt-4 pt-3 border-t border-slate-100 text-center">
                        <button 
                            onClick={onDeleteAccount} 
                            className="py-1 px-3 text-red-500 hover:text-red-700 text-xs font-semibold hover:bg-red-50 rounded-lg transition-colors"
                        >
                            Delete Account
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- EditOrgModal ---
export const EditOrgModal = ({ 
    org, 
    onClose, 
    onSave, 
    t,
    currentUser,
    allUsers,
    onTransferOwnership
}: { 
    org: Organization; 
    onClose: () => void; 
    onSave: (id: string, data: Partial<Organization>) => void; 
    t: (k: string) => string;
    currentUser?: UserProfile;
    allUsers?: UserProfile[];
    onTransferOwnership?: (params: {
        orgId: string;
        newAdminEmail: string;
        newAdminId?: string;
        newAdminName?: string;
        outgoingAdminRole: 'vice_principal' | 'teacher' | 'stepped_down';
        reason?: string;
        referenceTicket?: string;
        transferType?: 'self_serve' | 'super_admin_reassignment';
    }) => Promise<void>;
}) => {
    const [name, setName] = useState(org.name);
    const [address, setAddress] = useState(org.address);
    const [phone, setPhone] = useState(org.phone || '');
    const [photoUrl, setPhotoUrl] = useState(org.photoUrl);
    const [showTransferModal, setShowTransferModal] = useState(false);

    const isPrimaryPrincipal = currentUser ? currentUser.id === org.adminId : false;
    const primaryAdmin = allUsers ? allUsers.find(u => u.id === org.adminId) : (isPrimaryPrincipal ? currentUser : null);

    const handleSave = () => {
        onSave(org.id, { name, address, phone, photoUrl });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl font-bold mb-4">{t('edit_organization')}</h2>
                <div className="space-y-4">
                    <ImageUpload currentImage={photoUrl} onImageUpload={setPhotoUrl} label={t('organization_logo')} />
                    <input type="text" value={name} onChange={e => setName(sanitizeEntityName(e.target.value))} placeholder={t('organization_name')} className="w-full p-2 border rounded" />
                    <PhoneInput value={phone} onChange={setPhone} label={t('organization_phone')} />
                    <input type="text" value={address.street} onChange={e => setAddress({...address, street: e.target.value})} placeholder={t('street_address')} className="w-full p-2 border rounded" />
                    <div className="grid grid-cols-2 gap-2">
                        <input type="text" value={address.city} onChange={e => setAddress({...address, city: e.target.value})} placeholder={t('city')} className="w-full p-2 border rounded" />
                        <input type="text" value={address.state} onChange={e => setAddress({...address, state: e.target.value})} placeholder={t('state')} className="w-full p-2 border rounded" />
                    </div>
                    <input type="text" value={address.country} onChange={e => setAddress({...address, country: e.target.value})} placeholder={t('country')} className="w-full p-2 border rounded" />

                    {/* Primary Account Owner & Ownership Handover Option */}
                    <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl space-y-2 mt-2">
                        <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">
                                Primary Institution Owner
                            </span>
                            {isPrimaryPrincipal && onTransferOwnership && (
                                <button
                                    type="button"
                                    onClick={() => setShowTransferModal(true)}
                                    className="px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1 shadow-2xs cursor-pointer"
                                >
                                    <span className="material-icons-round text-xs">swap_horiz</span>
                                    <span>Transfer</span>
                                </button>
                            )}
                        </div>
                        <div className="text-xs">
                            <p className="font-bold text-slate-800">{primaryAdmin?.name || 'Primary Principal'}</p>
                            <p className="text-slate-500 font-mono text-[11px]">{primaryAdmin?.email || currentUser?.email || 'No email recorded'}</p>
                        </div>
                    </div>
                </div>
                <div className="flex gap-3 mt-6">
                    <button onClick={onClose} className="flex-1 py-3 bg-gray-100 rounded-lg font-bold">{t('cancel')}</button>
                    <button onClick={handleSave} className="flex-1 py-3 bg-indigo-600 text-white rounded-lg font-bold">{t('save')}</button>
                </div>
            </div>

            {/* Transfer Ownership Modal */}
            {showTransferModal && currentUser && allUsers && onTransferOwnership && (
                <TransferOwnershipModal
                    isOpen={showTransferModal}
                    onClose={() => setShowTransferModal(false)}
                    org={org}
                    currentUser={currentUser}
                    allUsers={allUsers}
                    onTransferOwnership={async (params) => {
                        await onTransferOwnership(params);
                        setShowTransferModal(false);
                        onClose();
                    }}
                    t={t}
                />
            )}
        </div>
    );
};

// --- AddChildModal ---
export const AddChildModal = ({ onClose, onAdd, t }: { onClose: () => void, onAdd: (name: string, code: string, gender?: Gender, photo?: string) => Promise<{success: boolean, message?: string}>, t: (k: string) => string }) => {
    const [code, setCode] = useState('');
    const [name, setName] = useState('');
    const [gender, setGender] = useState<Gender>('boy');
    const [photoUrl, setPhotoUrl] = useState<string>();
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        if (!photoUrl) {
            setError(t('photo_required') || 'Picture of the child is mandatory. Please upload a photo.');
            return;
        }
        if (!name.trim()) { 
            setError(t('name_required') || "Child's full name is required."); 
            return; 
        }
        if (!code.trim()) { 
            setError(t('code_required') || 'Invitation code is required.'); 
            return; 
        }
        setLoading(true);
        setError('');
        const res = await onAdd(name.trim(), code.trim(), gender, photoUrl);
        setLoading(false);
        if (res.success) onClose();
        else setError(res.message || 'Error adding child');
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                <h2 className="text-xl font-bold mb-4">{t('add_child')}</h2>
                <div className="space-y-4">
                    <ImageUpload 
                        onImageUpload={setPhotoUrl} 
                        currentImage={photoUrl} 
                        label={t('child_photo') || "Child's Photo"} 
                        required={true}
                    />
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">
                            {t('child_full_name')} <span className="text-red-500">*</span>
                        </label>
                        <input 
                            type="text" 
                            value={name} 
                            onChange={e => setName(sanitizePersonName(e.target.value))} 
                            placeholder={t('child_full_name')} 
                            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" 
                        />
                    </div>
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">
                            {t('gender')}
                        </label>
                        <GenderSelect value={gender} onChange={setGender} />
                    </div>
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">
                            {t('invitation_code')} <span className="text-red-500">*</span>
                        </label>
                        <input 
                            type="text" 
                            value={code} 
                            onChange={e => setCode(e.target.value.toUpperCase())} 
                            placeholder={t('invitation_code')} 
                            className="w-full p-3 border rounded-lg uppercase focus:ring-2 focus:ring-indigo-500 outline-none" 
                        />
                    </div>
                    {error && (
                        <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex items-center gap-2">
                            <span className="material-icons-round text-red-500 text-base">error_outline</span>
                            <span>{error}</span>
                        </div>
                    )}
                </div>
                <div className="flex gap-3 mt-6">
                    <button onClick={onClose} className="flex-1 py-3 bg-gray-100 rounded-lg font-bold hover:bg-gray-200 transition-colors">{t('cancel')}</button>
                    <button onClick={handleSubmit} disabled={loading} className="flex-1 py-3 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50">{loading ? t('processing') : t('send_join_request')}</button>
                </div>
            </div>
        </div>
    );
};

// --- EditStudentModal ---
export const EditStudentModal = ({ student, onClose, onSave, t }: { student: StudentConnection, onClose: () => void, onSave: (id: string, data: Partial<StudentConnection>) => void, t: (k: string) => string }) => {
    const [name, setName] = useState(student.childName);
    const [photoUrl, setPhotoUrl] = useState(student.photoUrl);
    const [gender, setGender] = useState(student.gender || 'boy');

    const handleSave = () => {
        onSave(student.id, { childName: name, photoUrl, gender });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                <h2 className="text-xl font-bold mb-4">{t('edit_child')}</h2>
                <div className="space-y-4">
                    <ImageUpload currentImage={photoUrl} onImageUpload={setPhotoUrl} label={t('your_photo')} />
                    <input type="text" value={name} onChange={e => setName(sanitizePersonName(e.target.value))} placeholder={t('child_full_name')} className="w-full p-3 border rounded-lg" />
                    <GenderSelect value={gender} onChange={setGender} />
                </div>
                <div className="flex gap-3 mt-6">
                    <button onClick={onClose} className="flex-1 py-3 bg-gray-100 rounded-lg font-bold">{t('cancel')}</button>
                    <button onClick={handleSave} className="flex-1 py-3 bg-indigo-600 text-white rounded-lg font-bold">{t('save')}</button>
                </div>
            </div>
        </div>
    );
};

// --- ParentInvitePrintModal (Print Preview & A4 Cuttable Tag Sheet) ---
export const ParentInvitePrintModal = ({
    codes,
    org,
    orgName,
    className,
    onClose,
    t
}: {
    codes: InvitationCode[];
    org?: Organization;
    orgName: string;
    className: string;
    onClose: () => void;
    t: (k: string, r?: any) => string;
}) => {
    const schoolName = org?.name || orgName || 'School';
    const schoolCode = org?.verification?.affiliationNumber || (org?.id ? org.id.slice(0, 8).toUpperCase() : 'EDU-ORG');
    const todayStr = new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-xs flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-auto p-4 sm:p-6 max-h-[95vh] flex flex-col border border-slate-200">
                {/* Print Control Header - Hidden on physical print */}
                <div className="no-print flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-slate-200 mb-4">
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="p-1.5 bg-emerald-100 text-emerald-700 rounded-lg material-icons-round text-xl">print</span>
                            <h2 className="text-lg sm:text-xl font-bold text-slate-800">
                                Printable Parent Invitation Slips ({codes.length})
                            </h2>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Class: <strong className="text-slate-700">{className}</strong> • Formatted for standard A4 paper (fits ~12–15 cuttable passes per sheet).
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handlePrint}
                            className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 shadow-sm transition-colors"
                        >
                            <span className="material-icons-round text-base">print</span>
                            Print Now (A4)
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
                            title="Close"
                        >
                            <span className="material-icons-round text-xl">close</span>
                        </button>
                    </div>
                </div>

                {/* Printable Container */}
                <div className="flex-1 overflow-y-auto pr-1">
                    {codes.length === 0 ? (
                        <p className="text-center text-slate-400 py-12">No codes selected to print.</p>
                    ) : (
                        <div className="printable-tags-sheet grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 print:grid-cols-3 print:gap-2.5 p-1">
                            {codes.map((code, idx) => {
                                const issueDate = code.createdAt 
                                    ? new Date(code.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
                                    : todayStr;
                                return (
                                    <div
                                        key={code.id || idx}
                                        className="printable-tag-card border-2 border-dashed border-slate-300 print:border-slate-800 rounded-xl p-3 bg-white print:bg-white flex flex-col justify-between h-full min-h-[175px] print:min-h-[160px] relative overflow-hidden shadow-xs print:shadow-none break-inside-avoid"
                                        style={{ pageBreakInside: 'avoid' }}
                                    >
                                        {/* Cut Corner Icon Indicator */}
                                        <div className="absolute top-1.5 right-1.5 text-[10px] text-slate-400 print:text-slate-500 font-mono flex items-center gap-0.5 select-none">
                                            <span>✂</span>
                                            <span className="text-[8px] uppercase tracking-tighter">cut</span>
                                        </div>

                                        {/* Header */}
                                        <div className="border-b border-slate-100 print:border-slate-300 pb-1.5 mb-1.5 pr-8">
                                            <div className="flex items-center gap-1.5">
                                                {org?.photoUrl ? (
                                                    <img src={org.photoUrl} alt="" className="w-5 h-5 rounded object-cover flex-shrink-0" />
                                                ) : (
                                                    <div className="w-5 h-5 rounded bg-emerald-50 text-emerald-600 flex items-center justify-center text-xs font-black">
                                                        🏫
                                                    </div>
                                                )}
                                                <h3 className="font-bold text-xs sm:text-sm text-slate-900 print:text-black line-clamp-1 leading-tight">
                                                    {schoolName}
                                                </h3>
                                            </div>
                                            <div className="flex items-center justify-between mt-1 text-[10px] text-slate-500 print:text-slate-700">
                                                <span className="font-semibold text-emerald-700 print:text-black bg-emerald-50 print:bg-slate-100 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider">
                                                    Parent & Child Pass
                                                </span>
                                                <span className="font-medium">
                                                    {className}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Main Code Box */}
                                        <div className="my-1 text-center bg-slate-50 print:bg-slate-100 border border-slate-200 print:border-slate-400 rounded-lg py-1.5 px-2">
                                            <div className="flex items-center justify-between text-[9px] text-slate-500 print:text-slate-700 uppercase font-semibold tracking-wider mb-0.5">
                                                <span>Parent Invite Code</span>
                                                <span>Issued: {issueDate}</span>
                                            </div>
                                            <p className="text-lg sm:text-xl font-black font-mono tracking-widest text-emerald-900 print:text-black select-all">
                                                {code.code}
                                            </p>
                                        </div>

                                        {/* Steps Instructions */}
                                        <div className="text-[9.5px] print:text-[9px] text-slate-600 print:text-slate-800 leading-tight space-y-0.5 my-1 bg-slate-50/50 p-1.5 rounded">
                                            <p className="font-bold text-slate-800 print:text-black">Quick Join Steps:</p>
                                            <p>1. Open EduGravity & sign in</p>
                                            <p>2. Select <strong>Parent</strong> role</p>
                                            <p>3. Enter child's name & code <strong className="text-slate-800 print:text-black">{code.code}</strong></p>
                                        </div>

                                        {/* Footer */}
                                        <div className="text-[8px] text-slate-400 print:text-slate-600 flex items-center justify-between pt-1 border-t border-slate-100 print:border-slate-200">
                                            <span>Single-use • Links 1 student</span>
                                            <span>EduGravity</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer in Modal */}
                <div className="no-print flex items-center justify-between pt-4 border-t border-slate-200 mt-2 text-xs text-slate-500">
                    <span>
                        Tip: Select <strong>A4</strong> paper and enable <strong>Background graphics</strong> in browser print settings.
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- InviteParentsModal (Parent & Student Invitation Manager) ---
export const InviteParentsModal = ({ 
    className, 
    orgName, 
    onClose, 
    onGenerate, 
    t,
    org,
    invitationCodes = [],
    classId,
    allUsers = [],
    allStudents = [],
    currentUser
}: { 
    className: string;
    orgName: string;
    onClose: () => void;
    onGenerate: (count: number) => Promise<InvitationCode[]>;
    t: (k: string, r?: any) => string;
    org?: Organization;
    invitationCodes?: InvitationCode[];
    classId?: string;
    allUsers?: UserProfile[];
    allStudents?: StudentConnection[];
    currentUser?: UserProfile;
}) => {
    const [generatedCodes, setGeneratedCodes] = useState<InvitationCode[]>([]);
    const [generateCount, setGenerateCount] = useState<number>(5);
    const [isGenerating, setIsGenerating] = useState(false);
    const [filterStatus, setFilterStatus] = useState<'unused' | 'all' | 'used'>('unused');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCodeIds, setSelectedCodeIds] = useState<Set<string>>(new Set());
    const [copyToast, setCopyToast] = useState<string | null>(null);
    const [printModalCodes, setPrintModalCodes] = useState<InvitationCode[] | null>(null);

    const schoolCode = org?.verification?.affiliationNumber || (org?.id ? org.id.slice(0, 8).toUpperCase() : 'SCH-CODE');

    // Policy check for teacher audit access
    const isPrincipal = currentUser?.role === 'principal' || currentUser?.role === 'vice_principal' || (currentUser as any)?.isVicePrincipal;
    const isAuditAllowed = isPrincipal || isStaffFeatureEnabled(org, 'allowTeacherInviteCodeAudit');

    // Resolve redemption audit details (linked student and parent profile)
    const resolveCodeDetails = React.useCallback((code: InvitationCode) => {
        const linkedStudent = allStudents.find(s => 
            (code.studentId && s.id === code.studentId) || 
            (s.invitationCodeId && s.invitationCodeId === code.id) || 
            (s.invitationCodeUsed && s.invitationCodeUsed.trim().toUpperCase() === code.code.trim().toUpperCase())
        );

        const parentUser = allUsers.find(u => 
            (code.usedByUserId && u.id === code.usedByUserId) || 
            (linkedStudent?.parentId && u.id === linkedStudent.parentId)
        );

        const studentName = code.studentName || linkedStudent?.name || (linkedStudent as any)?.childName || (code.studentId ? 'Enrolled Student' : null);
        const studentRoll = linkedStudent?.rollNumber || null;
        const studentGender = linkedStudent?.gender || null;
        const studentPhoto = linkedStudent?.photoUrl || null;

        const parentName = code.usedByName || parentUser?.name || linkedStudent?.parentName || (code.usedByUserId ? 'Registered Parent' : null);
        const parentEmail = code.usedByEmail || parentUser?.email || linkedStudent?.parentEmail || null;
        const parentPhone = parentUser?.mobileNumber || (parentUser as any)?.phone || null;

        const usedTimestamp = code.usedAt || (linkedStudent as any)?.enrolledAt || null;

        return {
            linkedStudent,
            parentUser,
            studentName,
            studentRoll,
            studentGender,
            studentPhoto,
            parentName,
            parentEmail,
            parentPhone,
            usedTimestamp,
            isLinked: Boolean(studentName || parentName || code.isUsed)
        };
    }, [allStudents, allUsers]);

    // Combine prop invitation codes and newly generated codes, removing duplicate IDs
    const allCodes = React.useMemo(() => {
        const map = new Map<string, InvitationCode>();
        // Add existing prop codes
        for (const c of invitationCodes) {
            map.set(c.id, c);
        }
        // Add freshly generated codes
        for (const c of generatedCodes) {
            map.set(c.id, c);
        }
        const list = Array.from(map.values());
        // Sort newest first
        list.sort((a, b) => {
            const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return timeB - timeA;
        });
        return list;
    }, [invitationCodes, generatedCodes]);

    const unusedCodes = allCodes.filter(c => !c.isUsed);
    const usedCodes = allCodes.filter(c => c.isUsed);

    const filteredCodes = React.useMemo(() => {
        let list = allCodes;
        if (filterStatus === 'unused') list = unusedCodes;
        else if (filterStatus === 'used') list = usedCodes;

        if (searchQuery.trim()) {
            const q = searchQuery.trim().toUpperCase();
            list = list.filter(c => {
                if (c.code.toUpperCase().includes(q)) return true;
                const details = resolveCodeDetails(c);
                if (details.studentName && details.studentName.toUpperCase().includes(q)) return true;
                if (details.parentName && details.parentName.toUpperCase().includes(q)) return true;
                if (details.parentEmail && details.parentEmail.toUpperCase().includes(q)) return true;
                if (details.studentRoll && details.studentRoll.toUpperCase().includes(q)) return true;
                return false;
            });
        }
        return list;
    }, [allCodes, filterStatus, searchQuery, unusedCodes, usedCodes, resolveCodeDetails]);

    const showToast = (msg: string) => {
        setCopyToast(msg);
        setTimeout(() => {
            setCopyToast(prev => prev === msg ? null : prev);
        }, 2800);
    };

    const handleGenerate = async (countToGen = generateCount) => {
        if (countToGen <= 0) return;
        setIsGenerating(true);
        try {
            const codes = await onGenerate(countToGen);
            setGeneratedCodes(prev => [...codes, ...prev]);
            showToast(`Generated ${codes.length} new parent invitation codes for ${className}!`);
        } catch (err) {
            console.error('Error generating parent codes:', err);
        } finally {
            setIsGenerating(false);
        }
    };

    // Copy single code
    const handleCopySingleCode = (code: string) => {
        navigator.clipboard.writeText(code);
        showToast(`Code "${code}" copied to clipboard!`);
    };

    // Copy audit record for used code
    const handleCopyAuditRecord = (code: InvitationCode) => {
        const details = resolveCodeDetails(code);
        const redeemedDate = details.usedTimestamp 
            ? new Date(details.usedTimestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
            : 'Recorded';
        const text = `Pass: ${code.code}\nClass: ${className}\nStudent: ${details.studentName || 'Not recorded'}${details.studentRoll ? ` (Roll: ${details.studentRoll})` : ''}\nParent: ${details.parentName || 'Parent account'}${details.parentEmail ? ` <${details.parentEmail}>` : ''}\nRedeemed At: ${redeemedDate}`;
        navigator.clipboard.writeText(text);
        showToast(`Redemption audit for code "${code.code}" copied to clipboard!`);
    };

    // Copy single formatted invite message for Parent
    const handleCopySingleInviteMessage = (code: InvitationCode) => {
        const issueDate = code.createdAt 
            ? new Date(code.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
            : new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
        
        const message = `🏫 *${orgName}* — Parent & Student Invitation Pass
📚 *Class:* ${className}
🔑 *Invite Code:* \`${code.code}\`
🏛️ *School Code:* ${schoolCode}
📅 *Issue Date:* ${issueDate}

📱 *How to connect:*
1. Open EduGravity (or download the app)
2. Sign in and select **Parent** as your role
3. Enter your child's name and this Invite Code: *${code.code}*
4. You will instantly get connected to ${className} for homework updates, attendance records, announcements, and direct messaging with teachers!`;

        navigator.clipboard.writeText(message);
        showToast(`Parent invite message for code "${code.code}" copied!`);
    };

    // Bulk copy plain codes
    const handleCopyBulkCodes = (targetCodes: InvitationCode[]) => {
        if (targetCodes.length === 0) return;
        const text = targetCodes.map(c => c.code).join('\n');
        navigator.clipboard.writeText(text);
        showToast(`${targetCodes.length} parent invitation codes copied!`);
    };

    // Bulk copy formatted invite message with list of codes
    const handleCopyBulkInviteMessage = (targetCodes: InvitationCode[]) => {
        if (targetCodes.length === 0) return;
        const codesList = targetCodes.map((c, i) => `${i + 1}. \`${c.code}\``).join('\n');
        const message = `🏫 *${orgName} — Parent Invitation Passes*
📚 *Class:* ${className}
🏛️ *School Code:* ${schoolCode}
📅 *Issue Date:* ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}

Parent & Student Invite Codes:
${codesList}

📱 *How to connect:*
1. Open EduGravity and sign in
2. Choose **Parent** role
3. Enter your student's name & one of the single-use invite codes above.
4. You'll immediately receive class announcements, homework, attendance, and progress updates!
(Note: Each code is single-use and links one child).`;

        navigator.clipboard.writeText(message);
        showToast(`Bulk parent invitation list copied!`);
    };

    // Toggle select code
    const toggleSelectCode = (id: string) => {
        setSelectedCodeIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Select all currently visible filtered codes
    const handleSelectAllFiltered = () => {
        if (selectedCodeIds.size >= filteredCodes.length && filteredCodes.length > 0) {
            setSelectedCodeIds(new Set());
        } else {
            setSelectedCodeIds(new Set(filteredCodes.map(c => c.id)));
        }
    };

    // Get selected objects
    const selectedCodesList = React.useMemo(() => {
        return allCodes.filter(c => selectedCodeIds.has(c.id));
    }, [allCodes, selectedCodeIds]);

    return (
        <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-3 sm:p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-5 sm:p-6 max-h-[92vh] flex flex-col border border-slate-100">
                    {/* Header */}
                    <div className="flex justify-between items-start pb-3 border-b border-slate-100 mb-4">
                        <div>
                            <div className="flex flex-wrap items-center gap-2">
                                <h2 className="text-xl font-bold text-slate-900">{t('invite_parents')}</h2>
                                <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 font-semibold text-xs rounded-full">
                                    {className}
                                </span>
                                <span className="px-2 py-0.5 bg-slate-100 text-slate-600 font-medium text-xs rounded-md">
                                    {orgName}
                                </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">
                                Single-use invitation codes for parents & students to connect to {className}.
                            </p>
                        </div>
                        <button 
                            type="button"
                            onClick={onClose}
                            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
                        >
                            <span className="material-icons-round text-xl">close</span>
                        </button>
                    </div>

                    {/* Toast Notification */}
                    {copyToast && (
                        <div className="mb-3 p-2.5 bg-emerald-600 text-white text-xs font-semibold rounded-xl flex items-center justify-between shadow-md animate-fade-in">
                            <div className="flex items-center gap-2">
                                <span className="material-icons-round text-sm">check_circle</span>
                                <span>{copyToast}</span>
                            </div>
                            <button onClick={() => setCopyToast(null)} className="text-white/80 hover:text-white">
                                <span className="material-icons-round text-xs">close</span>
                            </button>
                        </div>
                    )}

                    {/* Generator Box */}
                    <div className="bg-gradient-to-br from-emerald-50/70 to-slate-50 border border-emerald-100 rounded-2xl p-4 sm:p-5 mb-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                            <div>
                                <h3 className="font-bold text-sm text-slate-900 flex items-center gap-1.5">
                                    <span className="material-icons-round text-emerald-600 text-base">vpn_key</span>
                                    Generate Parent & Student Invite Codes
                                </h3>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    Create new single-use registration keys ready for print slips or WhatsApp distribution.
                                </p>
                            </div>

                            {/* Quick generation counts */}
                            <div className="flex items-center gap-1.5">
                                {[1, 5, 10, 20].map(cnt => (
                                    <button
                                        key={cnt}
                                        type="button"
                                        onClick={() => setGenerateCount(cnt)}
                                        className={`px-2.5 py-1 text-xs font-bold rounded-lg border transition-colors ${
                                            generateCount === cnt
                                                ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                                                : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-200'
                                        }`}
                                    >
                                        +{cnt}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2 bg-white px-3 py-1.5 border border-slate-200 rounded-xl">
                                <label className="text-xs text-slate-500 font-semibold">Count:</label>
                                <input
                                    type="number"
                                    min={1}
                                    max={50}
                                    value={generateCount}
                                    onChange={e => setGenerateCount(Math.max(1, parseInt(e.target.value) || 1))}
                                    className="w-14 font-mono font-bold text-sm text-slate-800 outline-none text-center"
                                />
                            </div>

                            <button
                                type="button"
                                onClick={() => handleGenerate(generateCount)}
                                disabled={isGenerating}
                                className="flex-1 sm:flex-none bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-5 py-2 rounded-xl font-bold text-xs sm:text-sm flex items-center justify-center gap-2 shadow-xs transition-colors disabled:opacity-60"
                            >
                                {isGenerating ? (
                                    <>
                                        <span className="material-icons-round animate-spin text-base">sync</span>
                                        <span>Generating...</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="material-icons-round text-base">add</span>
                                        <span>Generate {generateCount} Code{generateCount > 1 ? 's' : ''}</span>
                                    </>
                                )}
                            </button>

                            {unusedCodes.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setPrintModalCodes(unusedCodes)}
                                    className="bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded-xl font-bold text-xs sm:text-sm flex items-center gap-1.5 shadow-xs transition-colors ml-auto"
                                >
                                    <span className="material-icons-round text-base">print</span>
                                    <span>Print All Unused ({unusedCodes.length})</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Manage & Filter Codes Section */}
                    <div className="flex-1 overflow-y-auto pr-1 flex flex-col">
                        {/* Filter Bar & Search */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                            {/* Status Filter Tabs */}
                            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl text-xs font-semibold">
                                <button
                                    type="button"
                                    onClick={() => setFilterStatus('unused')}
                                    className={`px-3 py-1 rounded-lg transition-colors ${
                                        filterStatus === 'unused' ? 'bg-white text-slate-900 shadow-xs font-bold' : 'text-slate-600'
                                    }`}
                                >
                                    Unused ({unusedCodes.length})
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFilterStatus('all')}
                                    className={`px-3 py-1 rounded-lg transition-colors ${
                                        filterStatus === 'all' ? 'bg-white text-slate-900 shadow-xs font-bold' : 'text-slate-600'
                                    }`}
                                >
                                    All ({allCodes.length})
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFilterStatus('used')}
                                    className={`px-3 py-1 rounded-lg transition-colors ${
                                        filterStatus === 'used' ? 'bg-white text-slate-900 shadow-xs font-bold' : 'text-slate-600'
                                    }`}
                                >
                                    Used ({usedCodes.length})
                                </button>
                            </div>

                            {/* Search Filter */}
                            <div className="relative">
                                <span className="material-icons-round absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">search</span>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    placeholder={filterStatus === 'used' ? "Search student, parent, code..." : "Search code..."}
                                    className="w-full sm:w-56 pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs placeholder:text-slate-400 focus:bg-white transition-colors"
                                />
                            </div>
                        </div>

                        {/* Bulk Actions Toolbar */}
                        {filteredCodes.length > 0 && (
                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5 mb-3 flex flex-wrap items-center justify-between gap-2 text-xs">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="select-all-parent-codes"
                                        checked={selectedCodeIds.size > 0 && selectedCodeIds.size >= filteredCodes.length}
                                        onChange={handleSelectAllFiltered}
                                        className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300 cursor-pointer"
                                    />
                                    <label htmlFor="select-all-parent-codes" className="font-semibold text-slate-700 cursor-pointer select-none">
                                        {selectedCodeIds.size > 0 ? `${selectedCodeIds.size} Selected` : 'Select All'}
                                    </label>
                                </div>

                                <div className="flex flex-wrap items-center gap-2">
                                    {selectedCodeIds.size > 0 ? (
                                        <>
                                            <button
                                                type="button"
                                                onClick={() => handleCopyBulkCodes(selectedCodesList)}
                                                className="px-2.5 py-1 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 font-bold rounded-lg flex items-center gap-1 shadow-2xs transition-colors"
                                                title="Copy selected code strings"
                                            >
                                                <span className="material-icons-round text-sm">content_copy</span>
                                                <span>Copy Codes ({selectedCodesList.length})</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleCopyBulkInviteMessage(selectedCodesList)}
                                                className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-bold rounded-lg flex items-center gap-1 border border-emerald-200 transition-colors"
                                                title="Copy formatted WhatsApp invitation message"
                                            >
                                                <span className="material-icons-round text-sm">chat</span>
                                                <span>Copy WhatsApp Message</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setPrintModalCodes(selectedCodesList)}
                                                className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg flex items-center gap-1 shadow-2xs transition-colors"
                                                title="Print selected slips on A4 page"
                                            >
                                                <span className="material-icons-round text-sm">print</span>
                                                <span>Print Tags ({selectedCodesList.length})</span>
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            {unusedCodes.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleCopyBulkInviteMessage(unusedCodes)}
                                                    className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-bold rounded-lg flex items-center gap-1 border border-emerald-200 transition-colors"
                                                >
                                                    <span className="material-icons-round text-sm">chat</span>
                                                    <span>Copy All Unused as Message</span>
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Used Codes Audit Policy Guard */}
                        {filterStatus === 'used' && !isAuditAllowed ? (
                            <div className="p-8 bg-amber-50/70 border border-amber-200 rounded-2xl text-center my-4">
                                <span className="material-icons-round text-4xl text-amber-500 mb-2">lock</span>
                                <h4 className="text-sm font-bold text-amber-900">Audit Ledger Restricted</h4>
                                <p className="text-xs text-amber-700 mt-1 max-w-md mx-auto">
                                    Viewing redeemed invitation logs is restricted by your school’s current staff policies. Please contact your principal or school administrator if you need access.
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* Used Status Metrics Header */}
                                {filterStatus === 'used' && isAuditAllowed && usedCodes.length > 0 && (
                                    <div className="grid grid-cols-3 gap-2 mb-3">
                                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-center">
                                            <span className="text-[11px] font-bold text-slate-500 block">Redeemed Passes</span>
                                            <span className="text-base font-black text-slate-900">{usedCodes.length}</span>
                                        </div>
                                        <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl p-2.5 text-center">
                                            <span className="text-[11px] font-bold text-emerald-700 block">Students Enrolled</span>
                                            <span className="text-base font-black text-emerald-900">
                                                {new Set(usedCodes.map(c => resolveCodeDetails(c).studentName).filter(Boolean)).size}
                                            </span>
                                        </div>
                                        <div className="bg-indigo-50/60 border border-indigo-200 rounded-xl p-2.5 text-center">
                                            <span className="text-[11px] font-bold text-indigo-700 block">Parents Connected</span>
                                            <span className="text-base font-black text-indigo-900">
                                                {new Set(usedCodes.map(c => resolveCodeDetails(c).parentEmail || resolveCodeDetails(c).parentName).filter(Boolean)).size}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Codes List / Grid */}
                                <div className="flex-1">
                                    {filteredCodes.length === 0 ? (
                                        <div className="text-center py-12 text-slate-400 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                                            <span className="material-icons-round text-4xl mb-2 text-slate-300">vpn_key</span>
                                            <p className="font-medium text-sm text-slate-600">No invitation codes found.</p>
                                            <p className="text-xs text-slate-400 mt-1">
                                                {filterStatus === 'used' 
                                                    ? `No redeemed invitation passes match your search filter.`
                                                    : `Click "Generate Codes" above to create single-use registration passes for parents of ${className}.`
                                                }
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pb-2">
                                            {filteredCodes.map(code => {
                                                const isSelected = selectedCodeIds.has(code.id);
                                                const issueDate = code.createdAt 
                                                    ? new Date(code.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
                                                    : null;

                                                // If code is redeemed and audit is allowed, show rich audit card
                                                if (code.isUsed) {
                                                    const details = resolveCodeDetails(code);
                                                    const redeemedStr = details.usedTimestamp 
                                                        ? new Date(details.usedTimestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
                                                        : 'Recorded';

                                                    return (
                                                        <div
                                                            key={code.id}
                                                            className={`p-3.5 rounded-xl border transition-all flex flex-col gap-2.5 bg-white border-slate-200 hover:border-slate-300 hover:shadow-2xs ${
                                                                isSelected ? 'ring-2 ring-emerald-400/50' : ''
                                                            }`}
                                                        >
                                                            {/* Card Header */}
                                                            <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2">
                                                                <div className="flex items-center gap-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        onChange={() => toggleSelectCode(code.id)}
                                                                        className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300 cursor-pointer flex-shrink-0"
                                                                    />
                                                                    <span className="font-mono font-black text-sm tracking-wider text-slate-900 bg-slate-100 px-2 py-0.5 rounded border border-slate-200 select-all">
                                                                        {code.code}
                                                                    </span>
                                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold rounded-full">
                                                                        <span className="material-icons-round text-[12px]">check_circle</span>
                                                                        Redeemed
                                                                    </span>
                                                                </div>

                                                                <div className="flex items-center gap-1">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleCopySingleCode(code.code)}
                                                                        className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                                                                        title="Copy code"
                                                                    >
                                                                        <span className="material-icons-round text-base">content_copy</span>
                                                                    </button>
                                                                    {isAuditAllowed && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleCopyAuditRecord(code)}
                                                                            className="p-1.5 text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                                                                            title="Copy audit log"
                                                                        >
                                                                            <span className="material-icons-round text-base">assignment</span>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Details: Student & Parent Profile */}
                                                            {isAuditAllowed ? (
                                                                <div className="space-y-1.5 text-xs">
                                                                    {/* Student identity */}
                                                                    <div className="bg-slate-50 border border-slate-100 rounded-lg p-2 flex items-center gap-2.5">
                                                                        <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-xs flex-shrink-0">
                                                                            <span className="material-icons-round text-sm">face</span>
                                                                        </div>
                                                                        <div className="min-w-0 flex-1">
                                                                            <div className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">Student</div>
                                                                            <div className="font-bold text-slate-800 truncate">
                                                                                {details.studentName || 'Enrolled Student'}
                                                                            </div>
                                                                            {details.studentRoll && (
                                                                                <div className="text-[10px] text-slate-500">
                                                                                    Roll #{details.studentRoll}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    {/* Parent identity */}
                                                                    <div className="bg-slate-50 border border-slate-100 rounded-lg p-2 flex items-center gap-2.5">
                                                                        <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-800 flex items-center justify-center font-bold text-xs flex-shrink-0">
                                                                            <span className="material-icons-round text-sm">person</span>
                                                                        </div>
                                                                        <div className="min-w-0 flex-1">
                                                                            <div className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">Redeemed By Parent</div>
                                                                            <div className="font-bold text-slate-800 truncate">
                                                                                {details.parentName || 'Parent Account'}
                                                                            </div>
                                                                            {details.parentEmail && (
                                                                                <div className="text-[10px] text-slate-500 truncate" title={details.parentEmail}>
                                                                                    {details.parentEmail}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="text-[11px] text-slate-400 italic">
                                                                    Redeemed by student parent.
                                                                </div>
                                                            )}

                                                            {/* Card Footer: Timestamp */}
                                                            <div className="flex items-center justify-between text-[10px] text-slate-400 pt-1 border-t border-slate-50">
                                                                <span className="flex items-center gap-1">
                                                                    <span className="material-icons-round text-[12px] text-emerald-600">verified</span>
                                                                    <span>Redeemed: <strong className="text-slate-600 font-semibold">{redeemedStr}</strong></span>
                                                                </span>
                                                                {issueDate && <span>Issued: {issueDate}</span>}
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                // Unused / Ready Code Card
                                                return (
                                                    <div
                                                        key={code.id}
                                                        className={`p-3 rounded-xl border transition-all flex items-center justify-between gap-2 ${
                                                            isSelected
                                                                ? 'bg-emerald-50/60 border-emerald-300 shadow-xs'
                                                                : 'bg-white border-slate-200 hover:border-emerald-200 hover:shadow-2xs'
                                                        }`}
                                                    >
                                                        {/* Left: Checkbox + Code info */}
                                                        <div className="flex items-center gap-2.5 min-w-0">
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => toggleSelectCode(code.id)}
                                                                className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-slate-300 cursor-pointer flex-shrink-0"
                                                            />
                                                            <div className="min-w-0">
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className="font-mono font-black text-sm tracking-wider text-slate-900 select-all">
                                                                        {code.code}
                                                                    </span>
                                                                    <span className="px-1.5 py-0.2 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded">
                                                                        Ready
                                                                    </span>
                                                                </div>
                                                                <p className="text-[10px] text-slate-400">
                                                                    {issueDate ? `Created ${issueDate}` : 'Ready for parent'}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        {/* Right: Actions */}
                                                        <div className="flex items-center gap-1 flex-shrink-0">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCopySingleCode(code.code)}
                                                                className="p-1.5 text-slate-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                                                                title="Copy code"
                                                            >
                                                                <span className="material-icons-round text-base">content_copy</span>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCopySingleInviteMessage(code)}
                                                                className="p-1.5 text-slate-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                                                                title="Copy WhatsApp / Message formatted invite"
                                                            >
                                                                <span className="material-icons-round text-base">chat</span>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => setPrintModalCodes([code])}
                                                                className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                                                                title="Print cuttable slip for this student"
                                                            >
                                                                <span className="material-icons-round text-base">print</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100 mt-2 text-xs text-slate-500">
                        <span className="text-slate-500">
                            {unusedCodes.length} active pass{unusedCodes.length === 1 ? '' : 'es'} ready for distribution
                        </span>
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-colors"
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>

            {/* Print Modal for Parent Passes */}
            {printModalCodes && (
                <ParentInvitePrintModal
                    codes={printModalCodes}
                    org={org}
                    orgName={orgName}
                    className={className}
                    onClose={() => setPrintModalCodes(null)}
                    t={t}
                />
            )}
        </>
    );
};

// --- BroadcastModal ---
export const BroadcastModal = ({ onClose, classes, onSend, t, announcementToEdit, onUpdate }: { 
    onClose: () => void, 
    classes: ClassRoom[], 
    onSend: (title: string, text: string, imageUrl: string | undefined, classIds: string[]) => Promise<void>, 
    t: (k: string) => string,
    announcementToEdit?: Announcement | null,
    onUpdate?: (id: string, data: Partial<Announcement>) => Promise<void>
}) => {
    const [title, setTitle] = useState(announcementToEdit?.title || '');
    const [text, setText] = useState(announcementToEdit?.text || '');
    const [imageUrl, setImageUrl] = useState<string | undefined>(announcementToEdit?.imageUrls?.[0]);
    const [selectedClassIds, setSelectedClassIds] = useState<string[]>(announcementToEdit?.classIds || []);
    const [isSending, setIsSending] = useState(false);

    const handleSend = async () => {
        if (!title || !text) return;
        setIsSending(true);
        if (announcementToEdit && onUpdate) {
            await onUpdate(announcementToEdit.id, { title, text, imageUrls: imageUrl ? [imageUrl] : [], classIds: selectedClassIds });
        } else {
            await onSend(title, text, imageUrl, selectedClassIds);
        }
        setIsSending(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] flex flex-col">
                <h2 className="text-xl font-bold mb-4">{announcementToEdit ? t('edit') : t('broadcast_announcement')}</h2>
                <div className="flex-1 overflow-y-auto space-y-4">
                    <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Title" className="w-full p-3 border rounded-lg font-bold" />
                    <textarea value={text} onChange={e => setText(e.target.value)} placeholder={t('type_your_message')} className="w-full p-3 border rounded-lg min-h-[150px]" />
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">{t('send_to')}</label>
                        <div className="mt-2 space-y-2 max-h-32 overflow-y-auto border p-2 rounded">
                            <label className="flex items-center gap-2">
                                <input type="checkbox" checked={selectedClassIds.length === 0} onChange={() => setSelectedClassIds([])} />
                                <span className="font-bold">{t('all_classes')}</span>
                            </label>
                            {classes.map(c => (
                                <label key={c.id} className="flex items-center gap-2">
                                    <input 
                                        type="checkbox" 
                                        checked={selectedClassIds.includes(c.id)} 
                                        onChange={e => {
                                            if (e.target.checked) setSelectedClassIds([...selectedClassIds, c.id]);
                                            else setSelectedClassIds(selectedClassIds.filter(id => id !== c.id));
                                        }} 
                                    />
                                    <span>{c.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="mt-2">
                        <div className="flex items-center justify-between mb-1">
                            <label className="text-xs font-bold text-gray-500 uppercase">Attachment (Optional - Photos/PDFs under 5MB)</label>
                            {imageUrl && (
                                <button 
                                    type="button" 
                                    onClick={() => setImageUrl(undefined)} 
                                    className="text-xs text-red-500 hover:text-red-700 font-semibold"
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                        <AttachmentButton onImageSelect={setImageUrl} />
                        {imageUrl && (
                            <div className="relative inline-block mt-2">
                                {imageUrl.startsWith('data:application/pdf') || imageUrl.toLowerCase().includes('.pdf') ? (
                                    <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs font-bold">
                                        <span className="material-icons-round text-base">picture_as_pdf</span>
                                        <span>PDF Attached</span>
                                    </div>
                                ) : (
                                    <img src={imageUrl} alt="Preview" className="h-20 w-auto rounded border object-cover" />
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex gap-3 mt-6">
                    <button onClick={onClose} className="flex-1 py-3 bg-gray-100 rounded-lg font-bold">{t('cancel')}</button>
                    <button onClick={handleSend} disabled={isSending || !title || !text} className="flex-1 py-3 bg-orange-500 text-white rounded-lg font-bold disabled:opacity-50">
                        {isSending ? t('processing') : t('send')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- AssignTeachersModal ---
export const AssignTeachersModal = ({ onClose, cls, teachers, onSave, t }: { onClose: () => void, cls: ClassRoom, teachers: UserProfile[], onSave: (clsId: string, teacherIds: string[], classTeacherId?: string) => void, t: (k: string) => string }) => {
    const [selectedIds, setSelectedIds] = useState<string[]>(cls.teacherIds || []);
    const [classTeacherId, setClassTeacherId] = useState<string | undefined>(cls.classTeacherId);
    const [searchQuery, setSearchQuery] = useState('');

    const handleSave = () => {
        onSave(cls.id, selectedIds, classTeacherId);
        onClose();
    };

    const filteredTeachers = teachers.filter(teacher => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (teacher.name || '').toLowerCase().includes(q) || (teacher.email || '').toLowerCase().includes(q);
    });

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] flex flex-col border border-gray-100">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">{t('assign_teachers_to')} {cls.name}</h2>
                        <p className="text-xs text-gray-500 mt-0.5">Assign subject teachers and designate the primary class teacher.</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                        <span className="material-icons-round text-xl">close</span>
                    </button>
                </div>

                {teachers.length > 4 && (
                    <div className="relative mb-3">
                        <span className="material-icons-round absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">search</span>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Search by name or email..."
                            className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none"
                        />
                    </div>
                )}

                <div className="flex-1 overflow-y-auto space-y-2 pr-1 min-h-[160px]">
                    {filteredTeachers.length === 0 ? (
                        <div className="text-center py-8 text-gray-400 text-xs">
                            No matching instructors found.
                        </div>
                    ) : (
                        filteredTeachers.map(teacher => {
                            const isVP = teacher.isVicePrincipal;
                            const isPrincipal = teacher.role === 'principal' && !teacher.isVicePrincipal;
                            const isSelected = selectedIds.includes(teacher.id);

                            return (
                                <div 
                                    key={teacher.id} 
                                    className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${
                                        isSelected 
                                            ? 'border-indigo-200 bg-indigo-50/40' 
                                            : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    <label className="flex items-center gap-3 cursor-pointer flex-1 min-w-0">
                                        <input 
                                            type="checkbox" 
                                            checked={isSelected}
                                            onChange={e => {
                                                if (e.target.checked) setSelectedIds([...selectedIds, teacher.id]);
                                                else {
                                                    setSelectedIds(selectedIds.filter(id => id !== teacher.id));
                                                    if (classTeacherId === teacher.id) setClassTeacherId(undefined);
                                                }
                                            }}
                                            className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
                                        />
                                        <Avatar name={teacher.name} photoUrl={teacher.photoUrl} size="sm" />
                                        <div className="flex flex-col min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="text-sm font-semibold text-gray-900 truncate">{teacher.name}</span>
                                                {isVP ? (
                                                    <span className="text-[10px] font-bold text-teal-700 bg-teal-50 border border-teal-200 px-1.5 py-0.5 rounded-full">
                                                        Vice Principal
                                                    </span>
                                                ) : isPrincipal ? (
                                                    <span className="text-[10px] font-bold text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded-full">
                                                        Principal
                                                    </span>
                                                ) : (
                                                    <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">
                                                        Teacher
                                                    </span>
                                                )}
                                            </div>
                                            {teacher.email && <span className="text-xs text-gray-400 truncate">{teacher.email}</span>}
                                        </div>
                                    </label>
                                    {isSelected && (
                                        <label className={`text-xs flex items-center gap-1.5 cursor-pointer font-semibold ml-2 flex-shrink-0 px-2.5 py-1 rounded-lg border transition-all ${
                                            classTeacherId === teacher.id 
                                                ? 'bg-amber-100 text-amber-800 border-amber-300 shadow-xs' 
                                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'
                                        }`}>
                                            <input 
                                                type="radio" 
                                                name="classTeacher"
                                                checked={classTeacherId === teacher.id}
                                                onChange={() => setClassTeacherId(teacher.id)}
                                                className="text-amber-600 focus:ring-amber-500 cursor-pointer"
                                            />
                                            <span className="flex items-center gap-1">
                                                <span className={`material-icons-round text-xs ${classTeacherId === teacher.id ? 'text-amber-600' : 'text-gray-400'}`}>star</span>
                                                Class Teacher
                                            </span>
                                        </label>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
                    <button onClick={onClose} className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-sm transition-colors cursor-pointer">{t('cancel')}</button>
                    <button onClick={handleSave} className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm transition-colors shadow-sm cursor-pointer">{t('save')}</button>
                </div>
            </div>
        </div>
    );
};

// --- TeacherInvitePrintModal (Print Preview & A4 Tag Sheet) ---
export const TeacherInvitePrintModal = ({
    codes,
    org,
    onClose,
    t
}: {
    codes: InvitationCode[];
    org?: Organization;
    onClose: () => void;
    t: (k: string, r?: any) => string;
}) => {
    const schoolName = org?.name || 'School';
    const schoolCode = org?.verification?.affiliationNumber || (org?.id ? org.id.slice(0, 8).toUpperCase() : 'EDU-ORG');
    const todayStr = new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });

    const handlePrint = () => {
        window.print();
    };

    return (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-xs flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-auto p-4 sm:p-6 max-h-[95vh] flex flex-col border border-slate-200">
                {/* Print Control Header - Hidden on physical print */}
                <div className="no-print flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-slate-200 mb-4">
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="p-1.5 bg-indigo-100 text-indigo-700 rounded-lg material-icons-round text-xl">print</span>
                            <h2 className="text-lg sm:text-xl font-bold text-slate-800">
                                Printable Teacher Invitation Tags ({codes.length})
                            </h2>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Formatted in compact cuttable badges for standard A4 paper (fits ~12–15 cards per sheet).
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handlePrint}
                            className="bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 shadow-sm transition-colors"
                        >
                            <span className="material-icons-round text-base">print</span>
                            Print Now (A4)
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
                            title="Close"
                        >
                            <span className="material-icons-round text-xl">close</span>
                        </button>
                    </div>
                </div>

                {/* Printable Container */}
                <div className="flex-1 overflow-y-auto pr-1">
                    {codes.length === 0 ? (
                        <p className="text-center text-slate-400 py-12">No codes selected to print.</p>
                    ) : (
                        <div className="printable-tags-sheet grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 print:grid-cols-3 print:gap-2.5 p-1">
                            {codes.map((code, idx) => {
                                const issueDate = code.createdAt 
                                    ? new Date(code.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
                                    : todayStr;
                                return (
                                    <div
                                        key={code.id || idx}
                                        className="printable-tag-card border-2 border-dashed border-slate-300 print:border-slate-850 rounded-xl p-3 bg-white print:bg-white flex flex-col justify-between h-full min-h-[175px] print:min-h-[160px] relative overflow-hidden shadow-xs print:shadow-none break-inside-avoid"
                                        style={{ pageBreakInside: 'avoid' }}
                                    >
                                        {/* Cut Corner Icon Indicator */}
                                        <div className="absolute top-1.5 right-1.5 text-[10px] text-slate-400 print:text-slate-500 font-mono flex items-center gap-0.5 select-none">
                                            <span>✂</span>
                                            <span className="text-[8px] uppercase tracking-tighter">cut</span>
                                        </div>

                                        {/* Header */}
                                        <div className="border-b border-slate-100 print:border-slate-300 pb-1.5 mb-1.5 pr-8">
                                            <div className="flex items-center gap-1.5">
                                                {org?.photoUrl ? (
                                                    <img src={org.photoUrl} alt="" className="w-5 h-5 rounded object-cover flex-shrink-0" />
                                                ) : (
                                                    <div className="w-5 h-5 rounded bg-indigo-50 text-indigo-600 flex items-center justify-center text-xs font-black">
                                                        🏫
                                                    </div>
                                                )}
                                                <h3 className="font-bold text-xs sm:text-sm text-slate-900 print:text-black line-clamp-1 leading-tight">
                                                    {schoolName}
                                                </h3>
                                            </div>
                                            <div className="flex items-center justify-between mt-1 text-[10px] text-slate-500 print:text-slate-700">
                                                <span className="font-semibold text-indigo-700 print:text-black bg-indigo-50 print:bg-slate-100 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider">
                                                    Teacher Pass
                                                </span>
                                                <span className="font-medium">
                                                    Code: <strong className="text-slate-800 print:text-black">{schoolCode}</strong>
                                                </span>
                                            </div>
                                        </div>

                                        {/* Main Code Box */}
                                        <div className="my-1 text-center bg-slate-50 print:bg-slate-100 border border-slate-200 print:border-slate-400 rounded-lg py-1.5 px-2">
                                            <div className="flex items-center justify-between text-[9px] text-slate-500 print:text-slate-700 uppercase font-semibold tracking-wider mb-0.5">
                                                <span>Invite Code</span>
                                                <span>Issued: {issueDate}</span>
                                            </div>
                                            <p className="text-lg sm:text-xl font-black font-mono tracking-widest text-indigo-900 print:text-black select-all">
                                                {code.code}
                                            </p>
                                        </div>

                                        {/* Steps Instructions */}
                                        <div className="text-[9.5px] print:text-[9px] text-slate-600 print:text-slate-800 leading-tight space-y-0.5 my-1 bg-slate-50/50 p-1.5 rounded">
                                            <p className="font-bold text-slate-800 print:text-black">Quick Join Steps:</p>
                                            <p>1. Open EduGravity & sign in</p>
                                            <p>2. Select <strong>Teacher</strong> & enter this code</p>
                                        </div>

                                        {/* Footer */}
                                        <div className="text-[8px] text-slate-400 print:text-slate-600 flex items-center justify-between pt-1 border-t border-slate-100 print:border-slate-200">
                                            <span>One-time use • Keep safe</span>
                                            <span>EduGravity</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Footer in Modal */}
                <div className="no-print flex items-center justify-between pt-4 border-t border-slate-200 mt-2 text-xs text-slate-500">
                    <span>
                        Tip: Select <strong>A4</strong> paper and enable <strong>Background graphics</strong> in browser print settings.
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- StaffApprovalModal ---
export const StaffApprovalModal = ({ 
    onClose, 
    pendingTeachers, 
    activeTeachers, 
    onUpdateUser, 
    onGenerateInvitationCodes, 
    onCreatePendingTeacher, 
    t, 
    orgName,
    org,
    invitationCodes = [],
    allUsers = []
}: { 
    onClose: () => void, 
    pendingTeachers: UserProfile[], 
    activeTeachers: UserProfile[], 
    onUpdateUser: (id: string, data: Partial<UserProfile>) => void, 
    onGenerateInvitationCodes: (count: number) => Promise<InvitationCode[]>,
    onCreatePendingTeacher: (name: string, email: string) => void,
    t: (k: string, r?: any) => string,
    orgName: string,
    org?: Organization,
    invitationCodes?: InvitationCode[],
    allUsers?: UserProfile[]
}) => {
    const [activeTab, setActiveTab] = useState<'pending' | 'active' | 'invite'>('invite');
    const [inviteName, setInviteName] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [generatedCodes, setGeneratedCodes] = useState<InvitationCode[]>([]);
    const [generateCount, setGenerateCount] = useState<number>(5);
    const [isGenerating, setIsGenerating] = useState(false);
    const [filterStatus, setFilterStatus] = useState<'unused' | 'all' | 'used'>('unused');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCodeIds, setSelectedCodeIds] = useState<Set<string>>(new Set());
    const [copyToast, setCopyToast] = useState<string | null>(null);
    const [printModalCodes, setPrintModalCodes] = useState<InvitationCode[] | null>(null);
    const [showDirectEmailForm, setShowDirectEmailForm] = useState(false);

    const schoolCode = org?.verification?.affiliationNumber || (org?.id ? org.id.slice(0, 8).toUpperCase() : 'SCH-CODE');

    // Helper to resolve staff member details for teacher invitation pass
    const resolveTeacherCodeDetails = React.useCallback((code: InvitationCode) => {
        const teacher = allUsers.find(u => 
            (code.usedByUserId && u.id === code.usedByUserId) || 
            (u.invitationCodeId && u.invitationCodeId === code.id) || 
            (u.invitationCodeUsed && u.invitationCodeUsed.trim().toUpperCase() === code.code.trim().toUpperCase())
        ) || activeTeachers.find(u => u.id === code.usedByUserId) || pendingTeachers.find(u => u.id === code.usedByUserId);

        const teacherName = code.usedByName || teacher?.name || (code.usedByUserId ? 'Staff Member' : null);
        const teacherEmail = code.usedByEmail || teacher?.email || null;
        const teacherPhoto = teacher?.photoUrl || null;
        const teacherRole = teacher?.role === 'vice_principal' ? 'Vice Principal' : 'Teacher';
        const approvalStatus = teacher?.approvalStatus;
        const usedTimestamp = code.usedAt || teacher?.createdAt || null;

        return {
            teacher,
            teacherName,
            teacherEmail,
            teacherPhoto,
            teacherRole,
            approvalStatus,
            usedTimestamp,
            isLinked: Boolean(teacherName || code.isUsed)
        };
    }, [allUsers, activeTeachers, pendingTeachers]);

    // Combine prop invitation codes and newly generated codes, removing duplicate IDs
    const allCodes = React.useMemo(() => {
        const map = new Map<string, InvitationCode>();
        // Add existing prop codes
        for (const c of invitationCodes) {
            map.set(c.id, c);
        }
        // Add freshly generated codes
        for (const c of generatedCodes) {
            map.set(c.id, c);
        }
        const list = Array.from(map.values());
        // Sort newest first
        list.sort((a, b) => {
            const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return timeB - timeA;
        });
        return list;
    }, [invitationCodes, generatedCodes]);

    const unusedCodes = allCodes.filter(c => !c.isUsed);
    const usedCodes = allCodes.filter(c => c.isUsed);

    const filteredCodes = React.useMemo(() => {
        let list = allCodes;
        if (filterStatus === 'unused') list = unusedCodes;
        else if (filterStatus === 'used') list = usedCodes;

        if (searchQuery.trim()) {
            const q = searchQuery.trim().toUpperCase();
            list = list.filter(c => {
                if (c.code.toUpperCase().includes(q)) return true;
                const details = resolveTeacherCodeDetails(c);
                if (details.teacherName && details.teacherName.toUpperCase().includes(q)) return true;
                if (details.teacherEmail && details.teacherEmail.toUpperCase().includes(q)) return true;
                return false;
            });
        }
        return list;
    }, [allCodes, filterStatus, searchQuery, unusedCodes, usedCodes, resolveTeacherCodeDetails]);

    const showToast = (msg: string) => {
        setCopyToast(msg);
        setTimeout(() => {
            setCopyToast(prev => prev === msg ? null : prev);
        }, 2800);
    };

    // Copy audit record for used teacher pass
    const handleCopyTeacherAuditRecord = (code: InvitationCode) => {
        const details = resolveTeacherCodeDetails(code);
        const redeemedDate = details.usedTimestamp 
            ? new Date(details.usedTimestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
            : 'Recorded';
        const statusLabel = details.approvalStatus === 'approved' ? 'Active Approved' : details.approvalStatus === 'pending' ? 'Pending Approval' : 'Redeemed';
        const text = `Staff Pass: ${code.code}\nSchool: ${orgName}\nTeacher: ${details.teacherName || 'Staff Member'}${details.teacherEmail ? ` <${details.teacherEmail}>` : ''}\nRole: ${details.teacherRole}\nStatus: ${statusLabel}\nRedeemed At: ${redeemedDate}`;
        navigator.clipboard.writeText(text);
        showToast(`Staff audit record for code "${code.code}" copied to clipboard!`);
    };

    const handleGenerate = async (countToGen = generateCount) => {
        if (countToGen <= 0) return;
        setIsGenerating(true);
        try {
            const codes = await onGenerateInvitationCodes(countToGen);
            setGeneratedCodes(prev => [...codes, ...prev]);
            showToast(`Generated ${codes.length} new teacher invitation codes!`);
        } catch (err) {
            console.error('Error generating codes:', err);
        } finally {
            setIsGenerating(false);
        }
    };

    // Copy single code
    const handleCopySingleCode = (code: string) => {
        navigator.clipboard.writeText(code);
        showToast(`Code "${code}" copied to clipboard!`);
    };

    // Copy single formatted invite message
    const handleCopySingleInviteMessage = (code: InvitationCode) => {
        const issueDate = code.createdAt 
            ? new Date(code.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
            : new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
        
        const message = `🏫 *Welcome to ${orgName}!*
Here is your Teacher Invitation Pass to join our digital campus on EduGravity.

🔑 *Teacher Invite Code:* \`${code.code}\`
🏛️ *School Code / ID:* ${schoolCode}
📅 *Issue Date:* ${issueDate}

📱 *How to join:*
1. Open EduGravity (or download the app)
2. Sign in and select **Teacher** as your role
3. Enter your Invite Code: *${code.code}*
4. You will be immediately linked to ${orgName}!`;

        navigator.clipboard.writeText(message);
        showToast(`Formatted invite message for code "${code.code}" copied!`);
    };

    // Bulk copy plain codes
    const handleCopyBulkCodes = (targetCodes: InvitationCode[]) => {
        if (targetCodes.length === 0) return;
        const text = targetCodes.map(c => c.code).join('\n');
        navigator.clipboard.writeText(text);
        showToast(`${targetCodes.length} invitation codes copied to clipboard!`);
    };

    // Bulk copy formatted invite message with list of codes
    const handleCopyBulkInviteMessage = (targetCodes: InvitationCode[]) => {
        if (targetCodes.length === 0) return;
        const codesList = targetCodes.map((c, i) => `${i + 1}. \`${c.code}\``).join('\n');
        const message = `🏫 *Staff Invitation Passes — ${orgName}*
School Code: ${schoolCode}
Issue Date: ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}

Teacher Invitation Codes:
${codesList}

📱 *How to join:*
1. Open the EduGravity app & click Sign In
2. Select 'Teacher' role
3. Enter one of the unique invite codes above to link to ${orgName}.
(Note: Each code is single-use only).`;

        navigator.clipboard.writeText(message);
        showToast(`Bulk invitation list with instructions copied!`);
    };

    // Toggle select code
    const toggleSelectCode = (id: string) => {
        setSelectedCodeIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Select all currently visible filtered codes
    const handleSelectAllFiltered = () => {
        if (selectedCodeIds.size >= filteredCodes.length && filteredCodes.length > 0) {
            setSelectedCodeIds(new Set());
        } else {
            setSelectedCodeIds(new Set(filteredCodes.map(c => c.id)));
        }
    };

    // Get selected objects
    const selectedCodesList = React.useMemo(() => {
        return allCodes.filter(c => selectedCodeIds.has(c.id));
    }, [allCodes, selectedCodeIds]);

    return (
        <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-3 sm:p-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-5 sm:p-6 max-h-[92vh] flex flex-col border border-slate-100">
                    {/* Header */}
                    <div className="flex justify-between items-start pb-3 border-b border-slate-100 mb-4">
                        <div>
                            <div className="flex items-center gap-2">
                                <h2 className="text-xl font-bold text-slate-900">{t('manage_staff')}</h2>
                                <span className="px-2.5 py-0.5 bg-indigo-50 text-indigo-700 font-semibold text-xs rounded-full">
                                    {orgName}
                                </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5">
                                School Code: <strong className="text-slate-700 font-mono">{schoolCode}</strong>
                            </p>
                        </div>
                        <button 
                            type="button"
                            onClick={onClose}
                            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
                        >
                            <span className="material-icons-round text-xl">close</span>
                        </button>
                    </div>

                    {/* Toast Notification */}
                    {copyToast && (
                        <div className="mb-3 p-2.5 bg-emerald-600 text-white text-xs font-semibold rounded-xl flex items-center justify-between shadow-md animate-fade-in">
                            <div className="flex items-center gap-2">
                                <span className="material-icons-round text-sm">check_circle</span>
                                <span>{copyToast}</span>
                            </div>
                            <button onClick={() => setCopyToast(null)} className="text-white/80 hover:text-white">
                                <span className="material-icons-round text-xs">close</span>
                            </button>
                        </div>
                    )}
                    
                    {/* Navigation Tabs */}
                    <div className="flex bg-slate-100 p-1 rounded-xl mb-4 text-xs sm:text-sm">
                        <button 
                            type="button"
                            onClick={() => setActiveTab('invite')}
                            className={`flex-1 py-2 font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                                activeTab === 'invite' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            <span className="material-icons-round text-base">vpn_key</span>
                            <span>Invitation Codes</span>
                            {unusedCodes.length > 0 && (
                                <span className="px-1.5 py-0.2 bg-indigo-100 text-indigo-700 rounded-full text-[10px] font-bold">
                                    {unusedCodes.length}
                                </span>
                            )}
                        </button>
                        <button 
                            type="button"
                            onClick={() => setActiveTab('pending')}
                            className={`flex-1 py-2 font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                                activeTab === 'pending' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            <span className="material-icons-round text-base">pending_actions</span>
                            <span>{t('tab_pending')}</span>
                            {pendingTeachers.length > 0 && (
                                <span className="px-1.5 py-0.2 bg-amber-100 text-amber-800 rounded-full text-[10px] font-bold">
                                    {pendingTeachers.length}
                                </span>
                            )}
                        </button>
                        <button 
                            type="button"
                            onClick={() => setActiveTab('active')}
                            className={`flex-1 py-2 font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                                activeTab === 'active' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                            }`}
                        >
                            <span className="material-icons-round text-base">groups</span>
                            <span>{t('tab_active')} ({activeTeachers.length})</span>
                        </button>
                    </div>

                    {/* Tab Contents */}
                    <div className="flex-1 overflow-y-auto pr-1">
                        {/* 1. PENDING REQUESTS TAB */}
                        {activeTab === 'pending' && (
                            <div className="space-y-2">
                                {pendingTeachers.length === 0 ? (
                                    <div className="text-center py-12 text-slate-400">
                                        <span className="material-icons-round text-4xl mb-2 text-slate-300">verified</span>
                                        <p className="font-medium text-sm">No pending teacher requests.</p>
                                        <p className="text-xs text-slate-400 mt-1">Teachers who enter invite codes or request to join will appear here.</p>
                                    </div>
                                ) : (
                                    pendingTeachers.map(teacher => (
                                        <div key={teacher.id} className="flex items-center justify-between p-3.5 border border-slate-200 rounded-xl bg-white hover:border-indigo-200 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <Avatar name={teacher.name} photoUrl={teacher.photoUrl} size="md" />
                                                <div>
                                                    <p className="font-bold text-sm text-slate-900">{teacher.name}</p>
                                                    <p className="text-xs text-slate-500">{teacher.email || 'No email'}</p>
                                                    {teacher.mobileNumber && (
                                                        <p className="text-xs text-slate-400 font-mono">{teacher.mobileNumber}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button 
                                                    type="button"
                                                    onClick={() => onUpdateUser(teacher.id, { approvalStatus: 'approved' })} 
                                                    className="px-3 py-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-bold text-xs rounded-lg flex items-center gap-1 transition-colors"
                                                >
                                                    <span className="material-icons-round text-sm">check</span>
                                                    Approve
                                                </button>
                                                <button 
                                                    type="button"
                                                    onClick={() => onUpdateUser(teacher.id, { approvalStatus: 'rejected' })} 
                                                    className="px-3 py-1.5 bg-rose-50 text-rose-700 hover:bg-rose-100 font-bold text-xs rounded-lg flex items-center gap-1 transition-colors"
                                                >
                                                    <span className="material-icons-round text-sm">close</span>
                                                    Reject
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* 2. ACTIVE TEACHERS TAB */}
                        {activeTab === 'active' && (
                            <div className="space-y-2">
                                {activeTeachers.length === 0 ? (
                                    <div className="text-center py-12 text-slate-400">
                                        <span className="material-icons-round text-4xl mb-2 text-slate-300">person_add</span>
                                        <p className="font-medium text-sm">No active teachers linked yet.</p>
                                        <p className="text-xs text-slate-400 mt-1">Generate invitation codes to invite teachers to your school.</p>
                                    </div>
                                ) : (
                                    activeTeachers.map(teacher => (
                                        <div key={teacher.id} className="flex items-center justify-between p-3.5 border border-slate-200 rounded-xl bg-white hover:border-slate-300 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <Avatar name={teacher.name} photoUrl={teacher.photoUrl} size="md" />
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-bold text-sm text-slate-900">{teacher.name}</span>
                                                        <span className="px-2 py-0.2 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full">Active</span>
                                                    </div>
                                                    <p className="text-xs text-slate-500">{teacher.email || 'No email'}</p>
                                                </div>
                                            </div>
                                            <button 
                                                type="button"
                                                onClick={() => { 
                                                    if (window.confirm(t('confirm_remove_teacher', { teacherName: teacher.name }))) {
                                                        onUpdateUser(teacher.id, { approvalStatus: 'removed' }); 
                                                    }
                                                }} 
                                                className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                                                title="Remove teacher"
                                            >
                                                <span className="material-icons-round text-lg">delete</span>
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {/* 3. INVITATION CODES TAB */}
                        {activeTab === 'invite' && (
                            <div className="space-y-5">
                                {/* Code Generator Box */}
                                <div className="bg-gradient-to-br from-indigo-50/70 to-slate-50 border border-indigo-100 rounded-2xl p-4 sm:p-5">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                                        <div>
                                            <h3 className="font-bold text-sm text-slate-900 flex items-center gap-1.5">
                                                <span className="material-icons-round text-indigo-600 text-base">auto_awesome</span>
                                                Generate Teacher Invitation Codes
                                            </h3>
                                            <p className="text-xs text-slate-500 mt-0.5">
                                                Creates single-use registration keys formatted for quick distribution.
                                            </p>
                                        </div>

                                        {/* Quick generation counts */}
                                        <div className="flex items-center gap-1.5">
                                            {[1, 5, 10, 20].map(cnt => (
                                                <button
                                                    key={cnt}
                                                    type="button"
                                                    onClick={() => setGenerateCount(cnt)}
                                                    className={`px-2.5 py-1 text-xs font-bold rounded-lg border transition-colors ${
                                                        generateCount === cnt
                                                            ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                                                            : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-200'
                                                    }`}
                                                >
                                                    +{cnt}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-3">
                                        <div className="flex items-center gap-2 bg-white px-3 py-1.5 border border-slate-200 rounded-xl">
                                            <label className="text-xs text-slate-500 font-semibold">Count:</label>
                                            <input
                                                type="number"
                                                min={1}
                                                max={50}
                                                value={generateCount}
                                                onChange={e => setGenerateCount(Math.max(1, parseInt(e.target.value) || 1))}
                                                className="w-14 font-mono font-bold text-sm text-slate-800 outline-none text-center"
                                            />
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => handleGenerate(generateCount)}
                                            disabled={isGenerating}
                                            className="flex-1 sm:flex-none bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white px-5 py-2 rounded-xl font-bold text-xs sm:text-sm flex items-center justify-center gap-2 shadow-xs transition-colors disabled:opacity-60"
                                        >
                                            {isGenerating ? (
                                                <>
                                                    <span className="material-icons-round animate-spin text-base">sync</span>
                                                    <span>Generating...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="material-icons-round text-base">add</span>
                                                    <span>Generate {generateCount} Code{generateCount > 1 ? 's' : ''}</span>
                                                </>
                                            )}
                                        </button>

                                        {/* Direct Email Invite Toggle */}
                                        <button
                                            type="button"
                                            onClick={() => setShowDirectEmailForm(!showDirectEmailForm)}
                                            className="text-xs font-bold text-indigo-700 hover:text-indigo-900 bg-white border border-indigo-100 hover:bg-indigo-50/50 px-3 py-2 rounded-xl transition-colors flex items-center gap-1 ml-auto"
                                        >
                                            <span className="material-icons-round text-sm">mail</span>
                                            <span>{showDirectEmailForm ? 'Hide Direct Email' : 'Direct Email Invite'}</span>
                                        </button>
                                    </div>

                                    {/* Direct Email Form */}
                                    {showDirectEmailForm && (
                                        <div className="mt-4 pt-4 border-t border-indigo-100/80 bg-white/70 p-3 rounded-xl">
                                            <h4 className="text-xs font-bold text-slate-800 mb-2">Send Direct Email Invite</h4>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                                                <input
                                                    type="text"
                                                    value={inviteName}
                                                    onChange={e => setInviteName(e.target.value)}
                                                    placeholder={t('teacher_full_name')}
                                                    className="p-2 border border-slate-200 rounded-lg text-xs"
                                                />
                                                <input
                                                    type="email"
                                                    value={inviteEmail}
                                                    onChange={e => setInviteEmail(e.target.value)}
                                                    placeholder={t('teacher_email')}
                                                    className="p-2 border border-slate-200 rounded-lg text-xs"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (!inviteName.trim() || !inviteEmail.trim()) {
                                                        alert('Please enter name and email.');
                                                        return;
                                                    }
                                                    onCreatePendingTeacher(inviteName, inviteEmail);
                                                    setInviteName('');
                                                    setInviteEmail('');
                                                    showToast(`Invitation sent to ${inviteEmail}!`);
                                                }}
                                                className="bg-slate-800 hover:bg-slate-900 text-white px-3 py-1.5 rounded-lg font-bold text-xs transition-colors"
                                            >
                                                Send Email Invitation
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Manage & Filter Codes Section */}
                                <div>
                                    {/* Filter Bar & Search */}
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                                        {/* Status Filter Tabs */}
                                        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl text-xs font-semibold">
                                            <button
                                                type="button"
                                                onClick={() => setFilterStatus('unused')}
                                                className={`px-3 py-1 rounded-lg transition-colors ${
                                                    filterStatus === 'unused' ? 'bg-white text-slate-900 shadow-xs font-bold' : 'text-slate-600'
                                                }`}
                                            >
                                                Unused ({unusedCodes.length})
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setFilterStatus('all')}
                                                className={`px-3 py-1 rounded-lg transition-colors ${
                                                    filterStatus === 'all' ? 'bg-white text-slate-900 shadow-xs font-bold' : 'text-slate-600'
                                                }`}
                                            >
                                                All ({allCodes.length})
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setFilterStatus('used')}
                                                className={`px-3 py-1 rounded-lg transition-colors ${
                                                    filterStatus === 'used' ? 'bg-white text-slate-900 shadow-xs font-bold' : 'text-slate-600'
                                                }`}
                                            >
                                                Used ({usedCodes.length})
                                            </button>
                                        </div>

                                         {/* Search Filter */}
                                        <div className="relative">
                                            <span className="material-icons-round absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">search</span>
                                            <input
                                                type="text"
                                                value={searchQuery}
                                                onChange={e => setSearchQuery(e.target.value)}
                                                placeholder={filterStatus === 'used' ? "Search teacher name, email, code..." : "Search code..."}
                                                className="w-full sm:w-56 pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs placeholder:text-slate-400 focus:bg-white transition-colors"
                                            />
                                        </div>
                                    </div>

                                    {/* Used Status Metrics Header */}
                                    {filterStatus === 'used' && usedCodes.length > 0 && (
                                        <div className="grid grid-cols-2 gap-2 mb-3">
                                            <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-center">
                                                <span className="text-[11px] font-bold text-slate-500 block">Redeemed Staff Passes</span>
                                                <span className="text-base font-black text-slate-900">{usedCodes.length}</span>
                                            </div>
                                            <div className="bg-indigo-50/60 border border-indigo-200 rounded-xl p-2.5 text-center">
                                                <span className="text-[11px] font-bold text-indigo-700 block">Teachers Registered</span>
                                                <span className="text-base font-black text-indigo-900">
                                                    {new Set(usedCodes.map(c => resolveTeacherCodeDetails(c).teacherEmail || resolveTeacherCodeDetails(c).teacherName).filter(Boolean)).size}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Bulk Actions Toolbar */}
                                    {filteredCodes.length > 0 && (
                                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5 mb-3 flex flex-wrap items-center justify-between gap-2 text-xs">
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id="select-all-codes"
                                                    checked={selectedCodeIds.size > 0 && selectedCodeIds.size >= filteredCodes.length}
                                                    onChange={handleSelectAllFiltered}
                                                    className="w-4 h-4 text-indigo-600 rounded cursor-pointer"
                                                />
                                                <label htmlFor="select-all-codes" className="font-semibold text-slate-700 cursor-pointer select-none">
                                                    {selectedCodeIds.size > 0
                                                        ? `${selectedCodeIds.size} of ${filteredCodes.length} selected`
                                                        : `Select all (${filteredCodes.length})`}
                                                </label>
                                            </div>

                                            {/* Action Buttons: Bulk Copy & Print */}
                                            <div className="flex flex-wrap items-center gap-1.5">
                                                {/* Bulk Copy Codes */}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const target = selectedCodesList.length > 0 ? selectedCodesList : filteredCodes;
                                                        handleCopyBulkCodes(target);
                                                    }}
                                                    className="px-2.5 py-1 bg-white hover:bg-slate-100 border border-slate-200 text-slate-700 font-bold rounded-lg transition-colors flex items-center gap-1"
                                                    title="Copy codes list"
                                                >
                                                    <span className="material-icons-round text-sm">content_copy</span>
                                                    <span>Copy {selectedCodesList.length > 0 ? `(${selectedCodesList.length})` : 'All'}</span>
                                                </button>

                                                {/* Bulk Copy Formatted Message */}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const target = selectedCodesList.length > 0 ? selectedCodesList : filteredCodes;
                                                        handleCopyBulkInviteMessage(target);
                                                    }}
                                                    className="px-2.5 py-1 bg-white hover:bg-slate-100 border border-slate-200 text-indigo-700 font-bold rounded-lg transition-colors flex items-center gap-1"
                                                    title="Copy formatted WhatsApp/Email invite message"
                                                >
                                                    <span className="material-icons-round text-sm">chat</span>
                                                    <span>Copy Message</span>
                                                </button>

                                                {/* Bulk Print A4 Tags */}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const target = selectedCodesList.length > 0 ? selectedCodesList : filteredCodes;
                                                        setPrintModalCodes(target);
                                                    }}
                                                    className="px-3 py-1 bg-slate-800 hover:bg-slate-900 active:bg-black text-white font-bold rounded-lg transition-colors flex items-center gap-1 shadow-xs"
                                                    title="Print formatted A4 tags"
                                                >
                                                    <span className="material-icons-round text-sm">print</span>
                                                    <span>Print A4 Tags {selectedCodesList.length > 0 ? `(${selectedCodesList.length})` : `(${filteredCodes.length})`}</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Codes List */}
                                    {filteredCodes.length === 0 ? (
                                        <div className="text-center py-10 border border-dashed border-slate-200 rounded-xl">
                                            <span className="material-icons-round text-3xl text-slate-300 mb-1">tag</span>
                                            <p className="text-sm font-semibold text-slate-500">No invitation codes found.</p>
                                            <p className="text-xs text-slate-400 mt-0.5">
                                                {filterStatus === 'used'
                                                    ? 'No redeemed staff passes match your search query.'
                                                    : 'Click "Generate Codes" above to create some.'}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[420px] overflow-y-auto pr-1">
                                            {filteredCodes.map(c => {
                                                const isSelected = selectedCodeIds.has(c.id);
                                                const issueDate = c.createdAt 
                                                    ? new Date(c.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
                                                    : 'Recent';

                                                // If code is redeemed, show rich audit card
                                                if (c.isUsed) {
                                                    const details = resolveTeacherCodeDetails(c);
                                                    const redeemedStr = details.usedTimestamp
                                                        ? new Date(details.usedTimestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
                                                        : 'Recorded';

                                                    return (
                                                        <div
                                                            key={c.id}
                                                            className={`p-3.5 rounded-xl border transition-all flex flex-col gap-2.5 bg-white border-slate-200 hover:border-slate-300 hover:shadow-2xs ${
                                                                isSelected ? 'ring-2 ring-indigo-400/50' : ''
                                                            }`}
                                                        >
                                                            {/* Card Header */}
                                                            <div className="flex items-center justify-between gap-2 border-b border-slate-100 pb-2">
                                                                <div className="flex items-center gap-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        onChange={() => toggleSelectCode(c.id)}
                                                                        className="w-4 h-4 text-indigo-600 rounded cursor-pointer flex-shrink-0"
                                                                    />
                                                                    <span className="font-mono font-black text-sm text-indigo-900 bg-indigo-50/80 px-2 py-0.5 rounded border border-indigo-100 tracking-wider select-all">
                                                                        {c.code}
                                                                    </span>
                                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px] font-bold rounded-full">
                                                                        <span className="material-icons-round text-[12px]">check_circle</span>
                                                                        Redeemed
                                                                    </span>
                                                                </div>

                                                                <div className="flex items-center gap-1">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleCopySingleCode(c.code)}
                                                                        className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                                                                        title="Copy code"
                                                                    >
                                                                        <span className="material-icons-round text-base">content_copy</span>
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleCopyTeacherAuditRecord(c)}
                                                                        className="p-1.5 text-slate-400 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
                                                                        title="Copy staff audit record"
                                                                    >
                                                                        <span className="material-icons-round text-base">assignment</span>
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            {/* Details: Staff Member Profile */}
                                                            <div className="bg-slate-50 border border-slate-100 rounded-lg p-2.5 flex items-center gap-2.5">
                                                                {details.teacherPhoto ? (
                                                                    <img 
                                                                        src={details.teacherPhoto} 
                                                                        alt="" 
                                                                        className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-slate-200" 
                                                                    />
                                                                ) : (
                                                                    <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-800 flex items-center justify-center font-bold text-xs flex-shrink-0">
                                                                        <span className="material-icons-round text-base">person</span>
                                                                    </div>
                                                                )}
                                                                <div className="min-w-0 flex-1">
                                                                    <div className="flex items-center gap-1.5">
                                                                        <span className="font-bold text-slate-800 text-xs truncate">
                                                                            {details.teacherName || 'Staff Member'}
                                                                        </span>
                                                                        {details.approvalStatus === 'approved' ? (
                                                                            <span className="px-1.5 py-0.2 bg-emerald-100 text-emerald-800 text-[9px] font-bold rounded">
                                                                                Approved
                                                                            </span>
                                                                        ) : details.approvalStatus === 'pending' ? (
                                                                            <span className="px-1.5 py-0.2 bg-amber-100 text-amber-800 text-[9px] font-bold rounded">
                                                                                Pending
                                                                            </span>
                                                                        ) : null}
                                                                    </div>
                                                                    {details.teacherEmail && (
                                                                        <div className="text-[11px] text-slate-500 truncate" title={details.teacherEmail}>
                                                                            {details.teacherEmail}
                                                                        </div>
                                                                    )}
                                                                    <div className="text-[10px] text-indigo-600 font-semibold mt-0.5">
                                                                        {details.teacherRole}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Card Footer: Timestamp */}
                                                            <div className="flex items-center justify-between text-[10px] text-slate-400 pt-1 border-t border-slate-50">
                                                                <span className="flex items-center gap-1">
                                                                    <span className="material-icons-round text-[12px] text-indigo-600">verified</span>
                                                                    <span>Redeemed: <strong className="text-slate-600 font-semibold">{redeemedStr}</strong></span>
                                                                </span>
                                                                <span>Issued: {issueDate}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                // Unused / Active Code Card
                                                return (
                                                    <div 
                                                        key={c.id} 
                                                        className={`p-3 rounded-xl border transition-all flex items-center justify-between gap-3 ${
                                                            isSelected 
                                                                ? 'bg-indigo-50/50 border-indigo-300 shadow-xs' 
                                                                : 'bg-white border-slate-200 hover:border-slate-300'
                                                        }`}
                                                    >
                                                        {/* Checkbox & Code details */}
                                                        <div className="flex items-center gap-2.5 min-w-0">
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => toggleSelectCode(c.id)}
                                                                className="w-4 h-4 text-indigo-600 rounded cursor-pointer flex-shrink-0"
                                                            />
                                                            <div className="min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="font-mono font-black text-sm text-indigo-900 bg-indigo-50/80 px-2 py-0.5 rounded border border-indigo-100 tracking-wider select-all">
                                                                        {c.code}
                                                                    </span>
                                                                    <span className="px-1.5 py-0.2 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded">
                                                                        Active
                                                                    </span>
                                                                </div>
                                                                <p className="text-[10px] text-slate-400 mt-0.5">
                                                                    Issued: {issueDate} • School Code: <strong className="font-mono text-slate-600">{schoolCode}</strong>
                                                                </p>
                                                            </div>
                                                        </div>

                                                        {/* Actions: Copy & Print */}
                                                        <div className="flex items-center gap-1 flex-shrink-0">
                                                            {/* Copy Raw Code */}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCopySingleCode(c.code)}
                                                                className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                                                title="Copy code"
                                                            >
                                                                <span className="material-icons-round text-base">content_copy</span>
                                                            </button>

                                                            {/* Copy Formatted Message */}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCopySingleInviteMessage(c)}
                                                                className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                                                title="Copy invite message"
                                                            >
                                                                <span className="material-icons-round text-base">chat</span>
                                                            </button>

                                                            {/* Print Tag */}
                                                            <button
                                                                type="button"
                                                                onClick={() => setPrintModalCodes([c])}
                                                                className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                                                                title="Print single tag"
                                                            >
                                                                <span className="material-icons-round text-base">print</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Print Modal for A4 Tags */}
            {printModalCodes && (
                <TeacherInvitePrintModal
                    codes={printModalCodes}
                    org={org}
                    onClose={() => setPrintModalCodes(null)}
                    t={t}
                />
            )}
        </>
    );
};

// --- ManageAcademicYearModal ---
export const ManageAcademicYearModal = ({ yearToEdit, onClose, onSave, org, allAcademicYears, allClasses, onCreateClasses, t, isInitialSetup }: { 
    yearToEdit: AcademicYear | null, 
    onClose: () => void, 
    onSave: (year: any) => Promise<AcademicYear>, 
    org: Organization, 
    allAcademicYears: AcademicYear[], 
    allClasses: ClassRoom[], 
    onCreateClasses: (classes: any[], yearId: string) => Promise<any>, 
    t: (k: string) => string, 
    isInitialSetup: boolean 
}) => {
    // Helper to compute a sensible 1-year default end date (e.g., 2026-04-01 -> 2027-03-31)
    const computeDefaultEndDate = (start: string) => {
        if (!start) return '';
        try {
            const parts = start.split('-');
            if (parts.length === 3) {
                const y = parseInt(parts[0], 10);
                const m = parseInt(parts[1], 10) - 1;
                const d = parseInt(parts[2], 10);
                const dt = new Date(y, m, d);
                dt.setFullYear(dt.getFullYear() + 1);
                dt.setDate(dt.getDate() - 1);
                return dt.toISOString().split('T')[0];
            }
        } catch {
            // ignore
        }
        return '';
    };

    const initialStartDate = yearToEdit?.startDate || new Date().toISOString().split('T')[0];
    const [name, setName] = useState(yearToEdit?.name || '');
    const [startDate, setStartDate] = useState(initialStartDate);
    const [endDate, setEndDate] = useState(yearToEdit?.endDate || computeDefaultEndDate(initialStartDate));
    const [offSaturdays, setOffSaturdays] = useState<number[]>(yearToEdit?.offSaturdays || [2, 4]);
    const [copyFromYearId, setCopyFromYearId] = useState<string>('');
    const [step, setStep] = useState<1 | 2>(1);
    const [savedYear, setSavedYear] = useState<AcademicYear | null>(null);
    const [classesToCreate, setClassesToCreate] = useState<{name: string}[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (copyFromYearId) {
            const sourceClasses = allClasses.filter(c => c.academicYearId === copyFromYearId);
            if (sourceClasses.length > 0) {
                setClassesToCreate(sourceClasses.map(c => ({ name: c.name })));
            }
        } else if (!yearToEdit && classesToCreate.length === 0) {
            setClassesToCreate([{ name: 'Grade 1' }, { name: 'Grade 2' }]); // Sensible default suggestions
        }
    }, [copyFromYearId, allClasses]);

    // Handle Start Date change and suggest End Date if user hasn't customized
    const handleStartDateChange = (newStart: string) => {
        setStartDate(newStart);
        if (!endDate || endDate === computeDefaultEndDate(startDate)) {
            setEndDate(computeDefaultEndDate(newStart));
        }
    };

    const validateForm = () => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            setErrorMessage('Please enter an Academic Year / Session Name (e.g. 2026-27 or Fall 2026).');
            return false;
        }
        if (!startDate) {
            setErrorMessage('Please specify the session Start Date.');
            return false;
        }
        if (!endDate) {
            setErrorMessage('Please specify the session End Date.');
            return false;
        }
        if (new Date(endDate) < new Date(startDate)) {
            setErrorMessage('End Date must be after or on the Start Date.');
            return false;
        }
        setErrorMessage(null);
        return true;
    };

    /**
     * Handles saving the Academic Year document.
     * @param continueToClasses - If true, saves the year and advances to Step 2 for adding classes.
     *                            If false, saves the year and closes the modal.
     */
    const handleSaveAcademicYear = async (continueToClasses: boolean) => {
        if (!validateForm()) return;

        setIsSaving(true);
        setErrorMessage(null);

        try {
            const yearData = {
                ...(yearToEdit || {}),
                name: name.trim(),
                startDate,
                endDate,
                orgId: org.id,
                isActivated: yearToEdit ? yearToEdit.isActivated : allAcademicYears.length === 0, // Auto-activate if first
                offSaturdays
            };

            const saved = await onSave(yearData);
            setSavedYear(saved);

            if (continueToClasses) {
                // Advance to Step 2 (Class Setup)
                setStep(2);
            } else {
                // Finished saving, close modal
                onClose();
            }
        } catch (err: any) {
            console.error('Error saving academic year:', err);
            setErrorMessage(err?.message || 'Failed to save academic session. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Handles saving classes in Step 2 for the newly created academic year.
     */
    const handleSaveClasses = async () => {
        const targetYearId = savedYear?.id || yearToEdit?.id;
        if (!targetYearId) {
            onClose();
            return;
        }

        const validClasses = classesToCreate
            .map(c => ({ name: c.name.trim() }))
            .filter(c => c.name.length > 0);

        if (validClasses.length === 0) {
            onClose();
            return;
        }

        setIsSaving(true);
        setErrorMessage(null);

        try {
            await onCreateClasses(validClasses, targetYearId);
            onClose();
        } catch (err: any) {
            console.error('Error creating classes:', err);
            setErrorMessage(err?.message || 'Failed to create classes. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] flex flex-col border border-gray-100">
                {/* Header */}
                <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <span className="material-icons-round text-indigo-600">
                                {step === 1 ? 'calendar_today' : 'school'}
                            </span>
                            {yearToEdit ? 'Edit Academic Year' : (step === 1 ? 'Create Academic Year' : 'Add Classes')}
                        </h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {step === 1 
                                ? 'Define session duration, label, and working calendar.' 
                                : `Session '${savedYear?.name || name}' saved. Setup classes for this session.`}
                        </p>
                    </div>
                    <button 
                        onClick={onClose} 
                        className="text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                        title="Close"
                    >
                        <span className="material-icons-round text-lg">close</span>
                    </button>
                </div>

                {/* Error Banner */}
                {errorMessage && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2">
                        <span className="material-icons-round text-sm text-red-500 mt-0.5">error_outline</span>
                        <div className="flex-1 font-medium">{errorMessage}</div>
                        <button onClick={() => setErrorMessage(null)} className="text-red-400 hover:text-red-600">
                            <span className="material-icons-round text-xs">close</span>
                        </button>
                    </div>
                )}
                
                {step === 1 && (
                    <div className="space-y-4 overflow-y-auto flex-1 pr-1">
                        {/* Name Input with Full Flexibility */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                                Academic Year / Session Name <span className="text-red-500">*</span>
                            </label>
                            <input 
                                type="text" 
                                value={name} 
                                onChange={e => {
                                    setName(e.target.value);
                                    if (errorMessage) setErrorMessage(null);
                                }} 
                                placeholder={t('year_name_placeholder') || 'e.g. 2026-27, Fall 2026, or Foundation Batch 2026-28'} 
                                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm font-medium text-gray-900 bg-gray-50/50 hover:bg-white transition-colors" 
                            />
                            <p className="text-[11px] text-gray-500 mt-1">
                                Any session format supported: standard years (e.g. 2026-27), semester terms (Fall 2026-27), or coaching batches.
                            </p>
                        </div>

                        {/* Dates Row */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                                    {t('start_date')} <span className="text-red-500">*</span>
                                </label>
                                <input 
                                    type="date" 
                                    value={startDate} 
                                    onChange={e => handleStartDateChange(e.target.value)} 
                                    className="w-full p-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" 
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                                    {t('end_date')} <span className="text-red-500">*</span>
                                </label>
                                <input 
                                    type="date" 
                                    value={endDate} 
                                    onChange={e => {
                                        setEndDate(e.target.value);
                                        if (errorMessage) setErrorMessage(null);
                                    }} 
                                    className="w-full p-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" 
                                />
                            </div>
                        </div>

                        {/* Student Saturday Schedule (Instructional Calendar) */}
                        <div className="bg-indigo-50/60 p-3.5 rounded-xl border border-indigo-100 space-y-2">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                                <div>
                                    <label className="text-xs font-bold text-indigo-950 uppercase tracking-wide flex items-center gap-1.5">
                                        <span className="material-icons-round !text-sm text-indigo-600">event_note</span>
                                        Student Saturday Policy
                                    </label>
                                    <p className="text-[11px] text-indigo-700">
                                        Mark non-instructional Saturdays for students (excluded from working days)
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                    <button
                                        type="button"
                                        onClick={() => setOffSaturdays([])}
                                        className="text-[10px] px-2 py-0.5 rounded font-bold bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-100 transition-colors"
                                    >
                                        No Saturday Off
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setOffSaturdays([2, 4])}
                                        className="text-[10px] px-2 py-0.5 rounded font-bold bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-100 transition-colors"
                                    >
                                        2nd & 4th Off
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setOffSaturdays([1, 2, 3, 4, 5])}
                                        className="text-[10px] px-2 py-0.5 rounded font-bold bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-100 transition-colors"
                                    >
                                        All Saturdays Off
                                    </button>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 pt-1">
                                {[
                                    { num: 1, label: '1st Sat Off' },
                                    { num: 2, label: '2nd Sat Off' },
                                    { num: 3, label: '3rd Sat Off' },
                                    { num: 4, label: '4th Sat Off' },
                                    { num: 5, label: '5th Sat Off' }
                                ].map(sat => {
                                    const isOff = offSaturdays.includes(sat.num);
                                    return (
                                        <button
                                            key={sat.num}
                                            type="button"
                                            onClick={() => {
                                                if (isOff) setOffSaturdays(offSaturdays.filter(n => n !== sat.num));
                                                else setOffSaturdays([...offSaturdays, sat.num].sort());
                                            }}
                                            className={`flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-lg border text-xs font-semibold transition-all ${
                                                isOff
                                                    ? 'bg-indigo-600 text-white border-indigo-700 shadow-xs'
                                                    : 'bg-white text-gray-700 border-gray-200 hover:bg-indigo-50/50'
                                            }`}
                                        >
                                            <span className="material-icons-round !text-xs">
                                                {isOff ? 'check_box' : 'check_box_outline_blank'}
                                            </span>
                                            <span>{sat.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="pt-4 border-t border-gray-100 mt-6">
                            {yearToEdit ? (
                                /* Edit Mode: Cancel & Save Changes */
                                <div className="flex gap-3">
                                    <button 
                                        type="button"
                                        disabled={isSaving}
                                        onClick={onClose} 
                                        className="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-sm transition-colors disabled:opacity-50"
                                    >
                                        {t('cancel')}
                                    </button>
                                    <button 
                                        type="button"
                                        disabled={isSaving}
                                        onClick={() => handleSaveAcademicYear(false)} 
                                        className="flex-1 py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm shadow-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                                    >
                                        {isSaving ? (
                                            <>
                                                <span className="material-icons-round text-sm animate-spin">sync</span>
                                                Saving...
                                            </>
                                        ) : (
                                            t('save_changes') || 'Save Changes'
                                        )}
                                    </button>
                                </div>
                            ) : (
                                /* Create Mode: Cancel, Save, and Save & Continue to Add Classes */
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <button 
                                        type="button"
                                        disabled={isSaving}
                                        onClick={onClose} 
                                        className="py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-sm transition-colors disabled:opacity-50 order-3 sm:order-1"
                                    >
                                        {t('cancel')}
                                    </button>
                                    <button 
                                        type="button"
                                        disabled={isSaving}
                                        onClick={() => handleSaveAcademicYear(false)} 
                                        className="flex-1 py-2.5 px-4 bg-white border-2 border-indigo-600 text-indigo-700 hover:bg-indigo-50 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 order-2"
                                    >
                                        {isSaving ? (
                                            <span className="material-icons-round text-sm animate-spin">sync</span>
                                        ) : (
                                            <span className="material-icons-round text-sm">check</span>
                                        )}
                                        {t('save') || 'Save'}
                                    </button>
                                    <button 
                                        type="button"
                                        disabled={isSaving}
                                        onClick={() => handleSaveAcademicYear(true)} 
                                        className="flex-1 py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm shadow-sm transition-all flex items-center justify-center gap-1.5 disabled:opacity-50 order-1 sm:order-3"
                                    >
                                        {isSaving ? (
                                            <>
                                                <span className="material-icons-round text-sm animate-spin">sync</span>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <span>Save & Continue to Add Classes</span>
                                                <span className="material-icons-round text-sm">arrow_forward</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="flex-1 flex flex-col overflow-hidden">
                        {/* Session Saved Success Banner */}
                        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-3 flex items-center justify-between text-emerald-800 text-xs">
                            <div className="flex items-center gap-2">
                                <span className="material-icons-round text-base text-emerald-600">check_circle</span>
                                <div>
                                    <span className="font-bold">Academic Session Created!</span>
                                    <p className="text-emerald-700 text-[11px]">
                                        "{savedYear?.name || name}" is saved. You can add classes now or finish.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Copy Classes Option */}
                        {allAcademicYears.length > 0 && (
                            <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 mb-3">
                                <label className="flex items-center gap-2 font-bold text-xs text-gray-700 cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        checked={!!copyFromYearId} 
                                        onChange={e => setCopyFromYearId(e.target.checked ? allAcademicYears[0].id : '')} 
                                        className="rounded text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <span>{t('copy_class_structure')}</span>
                                </label>
                                {copyFromYearId && (
                                    <select 
                                        value={copyFromYearId} 
                                        onChange={e => setCopyFromYearId(e.target.value)} 
                                        className="w-full mt-2 p-2 text-xs border rounded-lg bg-white"
                                    >
                                        {allAcademicYears.map(y => (
                                            <option key={y.id} value={y.id}>Copy from: {y.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        )}

                        <div className="flex items-center justify-between mb-2">
                            <h3 className="font-bold text-xs uppercase tracking-wider text-gray-700">
                                {t('review_rename_classes')} ({classesToCreate.length})
                            </h3>
                            <button 
                                type="button"
                                onClick={() => setClassesToCreate([...classesToCreate, { name: '' }])} 
                                className="text-indigo-600 hover:text-indigo-700 font-bold text-xs flex items-center gap-1"
                            >
                                <span className="material-icons-round text-sm">add_circle</span>
                                Add Class
                            </button>
                        </div>

                        {/* Classes List */}
                        <div className="flex-1 overflow-y-auto space-y-2 border border-gray-200 p-2.5 rounded-xl mb-4 bg-gray-50/50">
                            {classesToCreate.length === 0 ? (
                                <div className="p-4 text-center text-xs text-gray-400">
                                    No classes added yet. Click "+ Add Class" above or click "Done / Skip" to finish.
                                </div>
                            ) : (
                                classesToCreate.map((cls, idx) => (
                                    <div key={idx} className="flex items-center gap-2 bg-white p-1.5 rounded-lg border border-gray-200 shadow-2xs">
                                        <span className="material-icons-round text-xs text-gray-400 ml-1">drag_indicator</span>
                                        <input 
                                            value={cls.name} 
                                            placeholder={`e.g. Grade ${idx + 1} or Section A`}
                                            onChange={e => {
                                                const newClasses = [...classesToCreate];
                                                newClasses[idx].name = e.target.value;
                                                setClassesToCreate(newClasses);
                                            }}
                                            className="flex-1 p-1.5 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-indigo-500"
                                        />
                                        <button 
                                            type="button"
                                            onClick={() => setClassesToCreate(classesToCreate.filter((_, i) => i !== idx))} 
                                            className="text-gray-400 hover:text-red-500 p-1 transition-colors"
                                            title="Remove Class"
                                        >
                                            <span className="material-icons-round text-sm">delete_outline</span>
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Step 2 Action Buttons */}
                        <div className="flex gap-3 pt-3 border-t border-gray-100 mt-auto">
                            <button 
                                type="button"
                                disabled={isSaving}
                                onClick={onClose} 
                                className="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-xs transition-colors disabled:opacity-50"
                            >
                                Done / Skip Classes
                            </button>
                            <button 
                                type="button"
                                disabled={isSaving || classesToCreate.filter(c => c.name.trim()).length === 0}
                                onClick={handleSaveClasses} 
                                className="flex-1 py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-xs shadow-sm transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {isSaving ? (
                                    <>
                                        <span className="material-icons-round text-sm animate-spin">sync</span>
                                        Creating Classes...
                                    </>
                                ) : (
                                    <>
                                        <span className="material-icons-round text-sm">save</span>
                                        Save Classes ({classesToCreate.filter(c => c.name.trim()).length})
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- CreateEditClassModal ---
export const CreateEditClassModal = ({ onClose, onSave, cls, t }: { onClose: () => void, onSave: (name: string) => void, cls: ClassRoom, t: (k: string) => string }) => {
    const [name, setName] = useState(cls.name || '');
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
                <h2 className="text-xl font-bold mb-4">{cls.id ? t('edit_class') : t('create_new_class')}</h2>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Class Name (e.g. Grade 5A)" className="w-full p-3 border rounded-lg mb-4" autoFocus />
                <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 py-3 bg-gray-100 rounded-lg font-bold">{t('cancel')}</button>
                    <button onClick={() => { if(name) { onSave(name); onClose(); } }} className="flex-1 py-3 bg-indigo-600 text-white rounded-lg font-bold">{t('save')}</button>
                </div>
            </div>
        </div>
    );
};

// --- CreateEditEventModal ---
export const CreateEditEventModal = ({ eventToEdit, onClose, onSave, classes, defaultClassId, user, academicYear, t }: { 
    eventToEdit: CalendarEvent | null, 
    onClose: () => void, 
    onSave: (evt: any) => Promise<void>, 
    classes: ClassRoom[], 
    defaultClassId?: string, 
    user: UserProfile, 
    academicYear?: AcademicYear, 
    t: (k: string) => string 
}) => {
    const [title, setTitle] = useState(eventToEdit?.title || '');
    const [type, setType] = useState<EventType>(eventToEdit?.type || 'class_event');
    const [startDate, setStartDate] = useState(eventToEdit?.startDate || new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(eventToEdit?.endDate || new Date().toISOString().split('T')[0]);
    const [description, setDescription] = useState(eventToEdit?.description || '');
    const [targetClassIds, setTargetClassIds] = useState<string[]>(eventToEdit?.targetClassIds || (defaultClassId ? [defaultClassId] : []));

    const handleSave = async () => {
        if (!title || !startDate || !academicYear) return;
        
        await onSave({
            ...(eventToEdit || {}),
            title, type, startDate, endDate, description, targetClassIds,
            academicYearId: academicYear.id,
            orgId: user.orgId!,
            creatorId: user.id
        });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] flex flex-col">
                <h2 className="text-xl font-bold mb-4">{eventToEdit ? t('edit_event') : t('add_event')}</h2>
                <div className="flex-1 overflow-y-auto space-y-4">
                    <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder={t('event_title')} className="w-full p-3 border rounded-lg font-bold" />
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold text-gray-500">{t('start_date')}</label>
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full p-3 border rounded-lg" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500">{t('end_date')}</label>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full p-3 border rounded-lg" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-gray-500">{t('event_type')}</label>
                        <select value={type} onChange={e => setType(e.target.value as EventType)} className="w-full p-3 border rounded-lg capitalize">
                            {['holiday', 'school_event', 'meeting', 'exam', 'deadline', 'class_event', 'urgent_off'].map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                        </select>
                    </div>
                    <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder={t('description')} className="w-full p-3 border rounded-lg" rows={3} />
                    
                    {user.role === 'principal' && (
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase">{t('send_to')}</label>
                            <div className="mt-2 space-y-2 max-h-32 overflow-y-auto border p-2 rounded">
                                <label className="flex items-center gap-2">
                                    <input type="checkbox" checked={targetClassIds.length === 0} onChange={() => setTargetClassIds([])} />
                                    <span className="font-bold">Whole School</span>
                                </label>
                                {classes.map(c => (
                                    <label key={c.id} className="flex items-center gap-2">
                                        <input 
                                            type="checkbox" 
                                            checked={targetClassIds.includes(c.id)} 
                                            onChange={e => {
                                                if (e.target.checked) setTargetClassIds([...targetClassIds, c.id]);
                                                else setTargetClassIds(targetClassIds.filter(id => id !== c.id));
                                            }} 
                                        />
                                        <span>{c.name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex gap-3 mt-6">
                    <button onClick={onClose} className="flex-1 py-3 bg-gray-100 rounded-lg font-bold">{t('cancel')}</button>
                    <button onClick={handleSave} className="flex-1 py-3 bg-indigo-600 text-white rounded-lg font-bold">{t('save')}</button>
                </div>
            </div>
        </div>
    );
};

// --- ReportAbsenceModal ---
export const ReportAbsenceModal = ({ existingAbsence, onClose, onSave, onDelete, t }: { 
    existingAbsence: PreReportedAbsence | null, 
    onClose: () => void, 
    onSave: (data: any) => void, 
    onDelete: (id: string) => void, 
    t: (k: string) => string 
}) => {
    const [date, setDate] = useState(existingAbsence?.date || new Date().toISOString().split('T')[0]);
    const [reason, setReason] = useState(existingAbsence?.reason || '');
    const [attachment, setAttachment] = useState(existingAbsence?.attachmentUrl);

    const handleSave = () => {
        if (!reason) return;
        onSave({ id: existingAbsence?.id, date, reason, attachment });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                <h2 className="text-xl font-bold mb-4">{existingAbsence ? 'Edit Absence' : t('report_absence')}</h2>
                <div className="space-y-4">
                    <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full p-3 border rounded-lg" min={new Date().toISOString().split('T')[0]} />
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">{t('reason_for_absence')}</label>
                        <select value={reason} onChange={e => setReason(e.target.value)} className="w-full p-3 border rounded-lg mb-2">
                            <option value="">Select Reason...</option>
                            <option value="Sick">{t('sick')}</option>
                            <option value="Family Event">{t('family_event')}</option>
                            <option value="Appointment">{t('appointment')}</option>
                            <option value="Other">{t('other')}</option>
                        </select>
                        <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Details..." className="w-full p-3 border rounded-lg" rows={2} />
                    </div>
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">Medical Note / Document</label>
                        <ImageUpload currentImage={attachment} onImageUpload={setAttachment} label="Upload" />
                    </div>
                </div>
                <div className="flex gap-3 mt-6">
                    {existingAbsence && <button onClick={() => onDelete(existingAbsence.id)} className="px-4 py-3 bg-red-100 text-red-600 rounded-lg"><span className="material-icons-round">delete</span></button>}
                    <button onClick={onClose} className="flex-1 py-3 bg-gray-100 rounded-lg font-bold">{t('cancel')}</button>
                    <button onClick={handleSave} className="flex-1 py-3 bg-indigo-600 text-white rounded-lg font-bold">{t('submit')}</button>
                </div>
            </div>
        </div>
    );
};

// --- SaturdayPolicyModal ---
export const SaturdayPolicyModal = ({
    activeYear,
    onClose,
    onSave,
    t
}: {
    activeYear: AcademicYear;
    onClose: () => void;
    onSave?: (updatedYear: AcademicYear) => Promise<void>;
    t: (k: string) => string;
}) => {
    const [offSaturdays, setOffSaturdays] = useState<number[]>(activeYear.offSaturdays || [2, 4]);
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        if (!onSave) {
            onClose();
            return;
        }
        setIsSaving(true);
        try {
            await onSave({
                ...activeYear,
                offSaturdays
            });
            onClose();
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 flex flex-col space-y-4">
                <div className="flex justify-between items-start">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <span className="material-icons-round text-indigo-600">weekend</span>
                            Student Saturday Schedule
                        </h2>
                        <p className="text-xs text-gray-500 mt-1">
                            Configure non-instructional Saturdays for students. Checked Saturdays are excluded from school working days and attendance calculations.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
                        <span className="material-icons-round">close</span>
                    </button>
                </div>

                <div className="bg-indigo-50/80 p-4 rounded-xl border border-indigo-100 space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-indigo-900 uppercase">Quick Presets:</span>
                        <div className="flex gap-1.5">
                            <button
                                type="button"
                                onClick={() => setOffSaturdays([])}
                                className="text-xs px-2.5 py-1 rounded-md font-bold bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-100 shadow-xs"
                            >
                                No Sat Off
                            </button>
                            <button
                                type="button"
                                onClick={() => setOffSaturdays([2, 4])}
                                className="text-xs px-2.5 py-1 rounded-md font-bold bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-100 shadow-xs"
                            >
                                2nd & 4th Off
                            </button>
                            <button
                                type="button"
                                onClick={() => setOffSaturdays([1, 2, 3, 4, 5])}
                                className="text-xs px-2.5 py-1 rounded-md font-bold bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-100 shadow-xs"
                            >
                                All Off (5-Day)
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 pt-2">
                        {[
                            { num: 1, label: '1st Saturday' },
                            { num: 2, label: '2nd Saturday' },
                            { num: 3, label: '3rd Saturday' },
                            { num: 4, label: '4th Saturday' },
                            { num: 5, label: '5th Saturday' }
                        ].map(sat => {
                            const isOff = offSaturdays.includes(sat.num);
                            return (
                                <button
                                    key={sat.num}
                                    type="button"
                                    onClick={() => {
                                        if (isOff) setOffSaturdays(offSaturdays.filter(n => n !== sat.num));
                                        else setOffSaturdays([...offSaturdays, sat.num].sort());
                                    }}
                                    className={`flex flex-col items-center justify-center p-3 rounded-xl border text-xs font-bold transition-all ${
                                        isOff
                                            ? 'bg-indigo-600 text-white border-indigo-700 shadow-md scale-[1.02]'
                                            : 'bg-white text-gray-700 border-gray-200 hover:bg-indigo-50/50'
                                    }`}
                                >
                                    <span className="material-icons-round text-base mb-1">
                                        {isOff ? 'check_circle' : 'radio_button_unchecked'}
                                    </span>
                                    <span>{sat.label}</span>
                                    <span className={`text-[10px] mt-0.5 ${isOff ? 'text-indigo-200' : 'text-gray-400'}`}>
                                        {isOff ? 'OFF (Holiday)' : 'Working'}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="bg-gray-50 p-3 rounded-xl border text-xs text-gray-600 flex items-start gap-2">
                    <span className="material-icons-round text-indigo-500 text-base flex-shrink-0">info</span>
                    <p>
                        This rule applies strictly to <strong>students' instructional calendar</strong>. Working days counter across parent, teacher, and principal ledgers will update automatically.
                    </p>
                </div>

                <div className="flex gap-3 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200"
                    >
                        {t('cancel')}
                    </button>
                    <button
                        type="button"
                        disabled={isSaving}
                        onClick={handleSave}
                        className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 shadow-md disabled:opacity-50"
                    >
                        {isSaving ? 'Saving...' : 'Save Saturday Policy'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export interface BulkHolidayRow {
    id: string;
    title: string;
    startDate: string;
    endDate: string;
    description: string;
}

// --- BulkAddHolidaysModal ---
export const BulkAddHolidaysModal = ({
    activeYear,
    user,
    onClose,
    onSaveBatch,
    t
}: {
    activeYear: AcademicYear;
    user: UserProfile;
    onClose: () => void;
    onSaveBatch: (events: Omit<CalendarEvent, 'id'>[]) => Promise<void>;
    t: (k: string) => string;
}) => {
    const yearStart = activeYear.startDate ? new Date(activeYear.startDate) : new Date();
    const currentYear = yearStart.getFullYear();

    const [rows, setRows] = useState<BulkHolidayRow[]>([
        {
            id: '1',
            title: 'Independence Day',
            startDate: `${currentYear}-08-15`,
            endDate: `${currentYear}-08-15`,
            description: 'Gazetted National Holiday'
        },
        {
            id: '2',
            title: 'Gandhi Jayanti',
            startDate: `${currentYear}-10-02`,
            endDate: `${currentYear}-10-02`,
            description: 'Gazetted National Holiday'
        },
        {
            id: '3',
            title: 'Republic Day',
            startDate: `${currentYear + 1}-01-26`,
            endDate: `${currentYear + 1}-01-26`,
            description: 'Gazetted National Holiday'
        }
    ]);

    const [isSaving, setIsSaving] = useState(false);

    const addRow = (preset?: Partial<BulkHolidayRow>) => {
        const newRow: BulkHolidayRow = {
            id: Math.random().toString(36).substring(2, 9),
            title: preset?.title || '',
            startDate: preset?.startDate || activeYear.startDate || new Date().toISOString().split('T')[0],
            endDate: preset?.endDate || preset?.startDate || activeYear.startDate || new Date().toISOString().split('T')[0],
            description: preset?.description || ''
        };
        setRows(prev => [...prev, newRow]);
    };

    const updateRow = (id: string, field: keyof BulkHolidayRow, value: string) => {
        setRows(prev => prev.map(r => {
            if (r.id !== id) return r;
            const updated = { ...r, [field]: value };
            // If startDate changed and was after endDate, bump endDate
            if (field === 'startDate' && updated.endDate < value) {
                updated.endDate = value;
            }
            return updated;
        }));
    };

    const removeRow = (id: string) => {
        setRows(prev => prev.filter(r => r.id !== id));
    };

    const handleSave = async () => {
        const validRows = rows.filter(r => r.title.trim() && r.startDate);
        if (validRows.length === 0) {
            alert('Please add at least one holiday with a title and start date.');
            return;
        }

        setIsSaving(true);
        try {
            const eventsToCreate: Omit<CalendarEvent, 'id'>[] = validRows.map(r => ({
                title: r.title.trim(),
                startDate: r.startDate,
                endDate: r.endDate || r.startDate,
                description: r.description.trim() || 'Official School Holiday',
                type: 'holiday',
                orgId: user.orgId!,
                academicYearId: activeYear.id,
                targetClassIds: [], // Whole school
                creatorId: user.id
            }));

            await onSaveBatch(eventsToCreate);
            onClose();
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl p-6 max-h-[92vh] flex flex-col">
                <div className="flex justify-between items-start pb-3 border-b">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <span className="material-icons-round text-blue-600">date_range</span>
                            Annual Student Holidays Setup (One-Shot Entry)
                        </h2>
                        <p className="text-xs text-gray-500 mt-1">
                            Set up national holidays and seasonal breaks for the year in one shot. These dates will automatically be marked non-instructional across the school calendar and attendance ledgers.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
                        <span className="material-icons-round">close</span>
                    </button>
                </div>

                {/* Quick Add Presets Bar */}
                <div className="py-3 bg-gray-50 px-3 rounded-xl my-3 border space-y-2">
                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Quick Add Standard Breaks:</span>
                    <div className="flex flex-wrap gap-1.5">
                        <button
                            type="button"
                            onClick={() => addRow({ title: 'Summer Vacation', description: 'Annual Summer Break' })}
                            className="text-xs px-2.5 py-1 bg-white border border-gray-300 hover:border-blue-500 hover:text-blue-600 rounded-lg font-medium transition-colors flex items-center gap-1"
                        >
                            <span className="material-icons-round !text-xs text-amber-500">wb_sunny</span>
                            + Summer Vacation
                        </button>
                        <button
                            type="button"
                            onClick={() => addRow({ title: 'Winter Break', description: 'Annual Winter Vacation' })}
                            className="text-xs px-2.5 py-1 bg-white border border-gray-300 hover:border-blue-500 hover:text-blue-600 rounded-lg font-medium transition-colors flex items-center gap-1"
                        >
                            <span className="material-icons-round !text-xs text-blue-400">ac_unit</span>
                            + Winter Break
                        </button>
                        <button
                            type="button"
                            onClick={() => addRow({ title: 'Diwali / Autumn Break', description: 'Festival Holidays' })}
                            className="text-xs px-2.5 py-1 bg-white border border-gray-300 hover:border-blue-500 hover:text-blue-600 rounded-lg font-medium transition-colors flex items-center gap-1"
                        >
                            <span className="material-icons-round !text-xs text-orange-500">flare</span>
                            + Diwali / Autumn Break
                        </button>
                        <button
                            type="button"
                            onClick={() => addRow({ title: 'Eid-ul-Fitr', description: 'Gazetted Holiday' })}
                            className="text-xs px-2.5 py-1 bg-white border border-gray-300 hover:border-blue-500 hover:text-blue-600 rounded-lg font-medium transition-colors flex items-center gap-1"
                        >
                            <span className="material-icons-round !text-xs text-emerald-500">nightlight</span>
                            + Eid-ul-Fitr
                        </button>
                        <button
                            type="button"
                            onClick={() => addRow({ title: 'Christmas / Year-End', description: 'Winter Holiday' })}
                            className="text-xs px-2.5 py-1 bg-white border border-gray-300 hover:border-blue-500 hover:text-blue-600 rounded-lg font-medium transition-colors flex items-center gap-1"
                        >
                            <span className="material-icons-round !text-xs text-red-500">celebration</span>
                            + Christmas / Year-End
                        </button>
                    </div>
                </div>

                {/* Rows List */}
                <div className="flex-1 overflow-y-auto space-y-3 pr-1">
                    {rows.map((row, index) => (
                        <div key={row.id} className="p-3 bg-white rounded-xl border border-gray-200 hover:border-blue-300 transition-colors shadow-xs space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-bold text-gray-500">Holiday #{index + 1}</span>
                                <button
                                    type="button"
                                    onClick={() => removeRow(row.id)}
                                    className="text-gray-400 hover:text-red-600 p-1 rounded-full"
                                    title="Remove this holiday"
                                >
                                    <span className="material-icons-round !text-base">delete</span>
                                </button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <div className="sm:col-span-1">
                                    <label className="text-[11px] font-bold text-gray-500">Holiday Name *</label>
                                    <input
                                        type="text"
                                        value={row.title}
                                        onChange={e => updateRow(row.id, 'title', e.target.value)}
                                        placeholder="e.g. Independence Day"
                                        className="w-full p-2 border rounded-lg text-sm font-semibold text-gray-800"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] font-bold text-gray-500">Start Date *</label>
                                    <input
                                        type="date"
                                        value={row.startDate}
                                        onChange={e => updateRow(row.id, 'startDate', e.target.value)}
                                        className="w-full p-2 border rounded-lg text-sm text-gray-800"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] font-bold text-gray-500">End Date</label>
                                    <input
                                        type="date"
                                        value={row.endDate}
                                        min={row.startDate}
                                        onChange={e => updateRow(row.id, 'endDate', e.target.value)}
                                        className="w-full p-2 border rounded-lg text-sm text-gray-800"
                                    />
                                </div>
                            </div>
                            <div>
                                <input
                                    type="text"
                                    value={row.description}
                                    onChange={e => updateRow(row.id, 'description', e.target.value)}
                                    placeholder="Optional note / description (e.g. Gazetted Holiday)"
                                    className="w-full p-2 border border-dashed rounded-lg text-xs text-gray-600 bg-gray-50/50"
                                />
                            </div>
                        </div>
                    ))}
                </div>

                <div className="pt-4 border-t mt-3 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={() => addRow()}
                        className="w-full sm:w-auto px-4 py-2.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-xl font-bold text-xs flex items-center justify-center gap-1.5 border border-blue-200"
                    >
                        <span className="material-icons-round !text-base">add</span>
                        Add Another Holiday
                    </button>
                    <div className="flex gap-2 w-full sm:w-auto">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 sm:flex-none px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-bold text-xs hover:bg-gray-200"
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="button"
                            disabled={isSaving || rows.length === 0}
                            onClick={handleSave}
                            className="flex-1 sm:flex-none px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            <span className="material-icons-round !text-base">save</span>
                            {isSaving ? 'Saving...' : `Save ${rows.filter(r => r.title.trim()).length} Holidays`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- PrincipalCalendarView ---
export const PrincipalCalendarView = ({ 
    user, 
    org, 
    activeYear, 
    classes, 
    calendarEvents, 
    onClose, 
    onCreateOrUpdate, 
    onDelete, 
    onUpdateAcademicYear,
    t, 
    setViewingImageUrl 
}: { 
    user: UserProfile, 
    org: Organization, 
    activeYear: AcademicYear, 
    classes: ClassRoom[], 
    calendarEvents: CalendarEvent[], 
    onClose: () => void, 
    onCreateOrUpdate: (evt: any) => Promise<void>, 
    onDelete: (id: string) => Promise<void>, 
    onUpdateAcademicYear?: (year: AcademicYear) => Promise<any>,
    t: (k: string) => string,
    setViewingImageUrl: (url: string | null) => void
}) => {
    const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showBulkHolidaysModal, setShowBulkHolidaysModal] = useState(false);
    const [showSaturdayModal, setShowSaturdayModal] = useState(false);

    const handleSaveBatchHolidays = async (events: Omit<CalendarEvent, 'id'>[]) => {
        for (const evt of events) {
            await onCreateOrUpdate(evt);
        }
    };

    const offSats = activeYear.offSaturdays || [2, 4];
    const saturdaySummary = offSats.length === 0 
        ? 'All Working' 
        : offSats.length === 5 
        ? 'All Off' 
        : `${offSats.map(s => `${s}${s===1?'st':s===2?'nd':s===3?'rd':'th'}`).join(', ')} Off`;

    return (
        <div className="fixed inset-0 bg-white z-40 flex flex-col">
            <div className="p-4 border-b flex flex-wrap justify-between items-center bg-gray-50 gap-3">
                <div className="flex items-center gap-2">
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full"><span className="material-icons-round">arrow_back</span></button>
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">{t('manage_calendar')}</h2>
                        <p className="text-xs text-gray-500">Student Instructional Calendar & Attendance Schedule</p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {/* Saturday Policy Button */}
                    <button 
                        onClick={() => setShowSaturdayModal(true)} 
                        className="bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100 px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors"
                        title="Configure student Saturday policy"
                    >
                        <span className="material-icons-round !text-base">weekend</span>
                        <span>Saturdays: {saturdaySummary}</span>
                    </button>

                    {/* Bulk Annual Holidays Setup */}
                    <button 
                        onClick={() => setShowBulkHolidaysModal(true)} 
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm transition-colors"
                        title="Add national holidays and annual breaks in one shot"
                    >
                        <span className="material-icons-round !text-base">date_range</span>
                        <span>Batch Annual Holidays</span>
                    </button>

                    {/* Add Single Event */}
                    <button 
                        onClick={() => { setEditingEvent(null); setShowModal(true); }} 
                        className="bg-gray-800 hover:bg-gray-900 text-white px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm transition-colors"
                    >
                        <span className="material-icons-round !text-base">add</span> 
                        <span>{t('add_event')}</span>
                    </button>
                </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
                <SmartCalendar 
                    user={user}
                    records={[]}
                    events={calendarEvents}
                    onEditEvent={(evt) => { setEditingEvent(evt); setShowModal(true); }}
                    onDeleteEvent={(id) => { if(window.confirm(t('confirm_delete_event'))) onDelete(id); }}
                    t={t}
                    setViewingImageUrl={setViewingImageUrl}
                    userLanguage={user.language}
                />
            </div>
            {showModal && (
                <CreateEditEventModal 
                    eventToEdit={editingEvent}
                    onClose={() => setShowModal(false)}
                    onSave={onCreateOrUpdate}
                    classes={classes}
                    user={user}
                    academicYear={activeYear}
                    t={t}
                />
            )}
            {showBulkHolidaysModal && (
                <BulkAddHolidaysModal
                    activeYear={activeYear}
                    user={user}
                    onClose={() => setShowBulkHolidaysModal(false)}
                    onSaveBatch={handleSaveBatchHolidays}
                    t={t}
                />
            )}
            {showSaturdayModal && (
                <SaturdayPolicyModal
                    activeYear={activeYear}
                    onClose={() => setShowSaturdayModal(false)}
                    onSave={onUpdateAcademicYear}
                    t={t}
                />
            )}
        </div>
    );
};

// --- WeeklyCheckinModal (Preserved & Enhanced with Full Multi-Language Translation) ---
export const WeeklyCheckinModal = ({ 
    onClose, 
    onSave, 
    t: parentT, 
    studentName,
    userLanguage = 'en'
}: { 
    onClose: () => void, 
    onSave: (data: any) => Promise<void>, 
    t: (k: string) => string, 
    studentName: string,
    userLanguage?: string
}) => {
    const {
        selectedLang,
        setSelectedLang,
        showOriginal,
        toggleShowOriginal,
        isTranslating,
        targetLangLabel,
        tCheckin
    } = useCheckinTranslation(userLanguage);

    const questions = [
        { key: 'studyStress', label: 'q_study_stress', sub: 'g_study_stress', options: ['opt_always', 'opt_mostly', 'opt_rarely', 'opt_not_at_all'] },
        { key: 'understanding', label: 'q_understanding', sub: 'g_understanding', options: ['opt_fully_understood', 'opt_mostly_understood', 'opt_struggled', 'opt_needed_help'] },
        { key: 'physicalActivity', label: 'q_physical_activity', sub: 'g_physical_activity', options: ['opt_daily', 'opt_4_5_days', 'opt_1_3_days', 'opt_no_little'] },
        { key: 'screenTime', label: 'q_screen_time', sub: 'g_screen_time', options: ['opt_yes_consistently', 'opt_most_days', 'opt_occasionally', 'opt_way_over'] },
        { key: 'dailyRoutine', label: 'q_daily_routine', sub: 'g_daily_routine', options: ['opt_very_consistent', 'opt_somewhat_consistent', 'opt_inconsistent'] },
        { key: 'socialEmotional', label: 'q_social_emotional', sub: 'g_social_emotional', options: ['opt_often', 'opt_sometimes', 'opt_rarely', 'opt_not_observed'] },
        { key: 'needsSupport', label: 'q_needs_support', sub: 'g_needs_support', options: ['opt_no_doing_well', 'opt_yes_discuss'] },
    ];

    // Store selected option keys (e.g. 'opt_always') so language toggles never lose selections
    const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
    const [details, setDetails] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const handleOption = (key: string, optKey: string) => {
        setSelectedOptions(prev => ({ ...prev, [key]: optKey }));
    };

    const isComplete = questions.every(q => selectedOptions[q.key]);

    const handleSave = async () => {
        if (!isComplete) return;
        setIsSaving(true);
        // Map option keys to canonical English values for seamless teacher reporting, class pulse, and AI insights
        const answers: Record<string, string> = {};
        questions.forEach(q => {
            const optKey = selectedOptions[q.key];
            answers[q.key] = OPTION_KEY_TO_ENGLISH[optKey] || tCheckin(optKey);
        });
        await onSave({ ...answers, details });
        setIsSaving(false);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] flex flex-col">
                <div className="mb-3">
                    <h2 className="text-xl font-bold mb-1 text-gray-800">{tCheckin('weekly_checkin')}</h2>
                    <p className="text-sm text-gray-500">{tCheckin('weekly_checkin_honesty_note')}</p>
                </div>

                {/* Translation Bar */}
                <div className="mb-4">
                    <CheckinTranslationBar
                        selectedLang={selectedLang}
                        onSelectLang={setSelectedLang}
                        showOriginal={showOriginal}
                        onToggleOriginal={toggleShowOriginal}
                        isTranslating={isTranslating}
                        targetLangLabel={targetLangLabel}
                    />
                </div>
                
                <div className="flex-1 overflow-y-auto space-y-6 pr-2">
                    {questions.map((q) => (
                        <div key={q.key} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="mb-3 flex items-start justify-between gap-2">
                                <div>
                                    <p className="font-bold text-gray-800">{tCheckin(q.label)}</p>
                                    <p className="text-sm text-gray-500 italic mt-1">{tCheckin(q.sub)}</p>
                                </div>
                                <ListenButton
                                    id={`checkin-q-${q.key}`}
                                    text={`${tCheckin(q.label)}. ${tCheckin(q.sub)}`}
                                    language={selectedLang}
                                    variant="inline"
                                    label="Listen"
                                    stopLabel="Stop"
                                    size="xs"
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                {q.options.map(opt => (
                                    <button
                                        key={opt}
                                        type="button"
                                        onClick={() => handleOption(q.key, opt)}
                                        className={`py-3 px-4 rounded-lg text-sm font-medium text-left transition-all ${
                                            selectedOptions[q.key] === opt 
                                                ? 'bg-indigo-600 text-white shadow-md border-indigo-600 font-bold' 
                                                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'
                                        }`}
                                    >
                                        {tCheckin(opt)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                    <div className="mt-4">
                        <label className="text-sm font-bold text-gray-600 block mb-1">{tCheckin('additional_details')}</label>
                        <textarea
                            value={details}
                            onChange={e => setDetails(e.target.value)}
                            className="w-full p-3 border rounded-lg h-24 text-sm focus:outline-none focus:border-indigo-500"
                            placeholder={tCheckin('additional_details_placeholder', 'Share any specific observations or concerns...')}
                            maxLength={500}
                        />
                        <p className="text-xs text-right text-gray-400 mt-1">{details.length}/500</p>
                    </div>
                </div>

                <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
                    <button 
                        type="button"
                        onClick={onClose} 
                        className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold transition-colors"
                    >
                        {tCheckin('cancel')}
                    </button>
                    <button 
                        type="button"
                        onClick={handleSave} 
                        disabled={!isComplete || isSaving} 
                        className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-bold disabled:opacity-50 transition-colors shadow-sm"
                    >
                        {isSaving ? tCheckin('processing') : tCheckin('submit')}
                    </button>
                </div>
            </div>
        </div>
    );
};
